import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import moment from 'moment';
import isEqual from '@wry/equality';
import {
  Badge,
  Box,
  Button,
  DatePicker,
  DeleteButton,
  DropdownButton,
  FilterBarDateRangePicker,
  Input,
  Menu,
  MenuItem,
  OptimisticCheckbox,
  Select,
  Table,
  TableCell,
  TableHeader,
  TableHeaders,
  TableRow,
  Text,
  TimeField,
  Tooltip,
  View,
} from '../ui';
import Heading from '../ui/Heading';
import { useModal } from '../contexts/ModalContext';
import type {
  CalculateVoyageInputs,
  FuelInput,
  FuelSummaries,
  ShippingPlan_ShipFragment,
  VoyageCalculation,
  VoyageNamingRule_AdminFragment,
} from '../../graphql';
import {
  useCalculateVoyagesQuery,
  useUpdateShipmentMutation,
} from '../../graphql';
import {
  EventCategory,
  getSofAndNoticeOverridesFromPortCall,
  minimumEventDuration,
  SwapDirection,
} from '../core/Core';
import type {
  LoadingRateSource,
  Event,
  Leg,
  SingleEvent,
  TCOrderEvent,
  EventOrLeg,
  Events,
  EventsAndLegs,
  PortCallArrivalEvent,
  PortCallEvent,
  WaitEvent,
  TimeDataInputs,
  TimeDataTotals,
  TimeData,
} from '../core/TimeData';
import {
  getEventLocationId,
  CoreEventType,
  PortCallEventType,
  SingleEventType,
  isDischargeEvent,
  isLoadEvent,
  isPortCallEvent,
  isSingleEvent,
  isTCOrderEvent,
  isLeg,
  isPortCallBerthingEventTimeData,
  isPortCallDepartureEventTimeData,
  isTimeData,
  ImplicitEventType,
  isPortCallArrivalEvent,
  isPortCallDepartureEvent,
  getTimedataDurations,
  initTotals,
  isCargoEventTimeData,
  isPortCallEventTimeData,
} from '../core/TimeData';
import type { ScheduleDataContext } from '../core/ScheduleDataContext';
import { hToMs, msInH, msToH } from '../../lib/time';

import {
  defaultLoadingUnit,
  loadingRateSourceToLabel,
  loadingUnitToName,
} from '../../constants/loadingUnits';
import { AlertIcon } from '../Icons';
import { useTheme } from 'styled-components';
import {
  assertDefined,
  assertedCast,
  assertedDefined,
  exhaustiveCheck,
  get,
  groupBySequence,
  interleave,
  InterleaveItemResult,
  isDefined,
  mapKeyBy,
  sortBy,
  takeWhileFrom,
  withDefined,
} from '../../lib/util';
import groupBy from 'lodash/groupBy';
import { useLocalSetting } from '../hooks/useLocalConfig';
import type { Cell, CellProps, Column, Row } from 'react-table';
import { useSortBy, useTable } from 'react-table';
import type { DatePickerProps } from '../ui/DatePicker/DatePicker';
import capitalize from 'lodash/capitalize';
import type {
  EventTimeline,
  TimelineFocus,
  VoyageGroup,
} from '../core/Timelines';
import type { ScenarioTimelineProps } from '../timeline/ScenarioTimeline';
import type { vesselFilterProps } from './VesselChangeForm';
import VesselChangeForm from './VesselChangeForm';
import { getOrderDisplayName, parseAmountInfo } from '../order/order';
import { TableColumnsSelector } from '../../pages/orders';
import { oneDecimal } from './useUpdatingDuration';
import { find, noop, sum, uniq } from 'lodash';
import { DurationInput } from '../DurationInput';
import type { ClientId } from '../core/ClientId';
import { useBerthPrediction } from '../integrations/BerthPrediction';
import {
  getPortCallSourceNameForPortCall,
  getPortCallTimeSourceType,
  PortCallUpdateMenuItem,
  usePortCallUpdates,
} from '../PortCallUpdates';
import { useRouter } from 'next/router';
import type { CachedBerthPrediction } from '../core/BerthPrediction';
import useContextMenu from '../hooks/useContextMenu';
import { timeFieldFormat } from '../ui/TimeField';
import type { ShipmentVoyageCharterUpdatesProps } from './VoyageCharterUpdate';
import { ShipmentVoyageCharterUpdates } from './VoyageCharterUpdate';
import type { LocationSelectValue } from '../LocationSelect';
import LocationSelect from '../LocationSelect';
import { useDistances, useSingleDistance } from '../distances/hooks';
import { singleEventTypes } from './ShippingPlanTypes';
import {
  fetchShipmentNames,
  getShipmentPrefix,
  getShipmentPrefixWithRule,
} from '../ScenarioUtil';
import { getOrderAssignments } from '@seaber/common/core/scenario';
import { useApolloClient } from '@apollo/client';
import type {
  BackupSchedule,
  InitialSchedule,
  MasterSchedule,
  ScenarioSchedule,
  SuggestionSchedule,
  UserSchedule,
} from '@seaber/common/core/scenario';
import type {
  ScenarioLocation,
  ScenarioOrder,
  ScenarioShipment,
} from '../../lib/planningData';
import { LocationCell } from '../order/OrderTableUtils';
import { useBunkerPrices } from '../hooks/useBunkerPrices';
import useEvent from '../hooks/useEvent';
import type { User } from '../../state/zustand';
import { hasFeature, usePlanningMode } from '../../state/zustand';
import { useViolationIndicatorBasisState } from '../../state/planning/options';
import { useVoyageNamingRules } from '../hooks/useVoyageNamingRules';
import {
  getCargoUnitQuantity,
  systemAmountToCargoUnit,
} from '../../constants/cargoUnits';
import { atom, useAtom } from 'jotai';
import type { DefaultMap } from '@seaber/common/lib/collections';
import { formatChannelName } from '@seaber/common/core/channels';
import { getDraftWarningAtEvent } from '../../components/timeline/utils/VesselDraftUtils';
import { VesselDraftWarning } from '../timeline/VesselDraftWarning';
import { NumericInput } from '../NumericInput';

type TimeViolationType =
  | 'order_reqs'
  | 'physical_conflict'
  | 'override_conflict';

const getTimeViolationType = (
  cancellingOrDeliveryViolation: number,
  physicalTimeConflict?: number,
  startOverrideConflict?: number,
): TimeViolationType | null => {
  if ((startOverrideConflict ?? 0) > hToMs(0.1)) {
    return 'override_conflict';
  } else if ((physicalTimeConflict ?? 0) > hToMs(0.1)) {
    return 'physical_conflict';
  } else if (cancellingOrDeliveryViolation > 0.1) {
    return 'order_reqs';
  } else {
    return null;
  }
};

const generateViolationTitleText = (
  violation: TimeViolationType | null,
  time: { value: number; timeZone: string | undefined } | null,
): string | undefined => {
  const timeStr = time && timeFieldFormat(time.value, time.timeZone);
  if (violation === 'override_conflict') {
    return `There is a timing override which cannot be met: ${timeStr}`;
  } else if (violation === 'physical_conflict') {
    return 'The vessel cannot physically reach this destination by the given time';
  } else if (violation === 'order_reqs') {
    return 'The schedule does not meet the order requirements';
  } else if (violation === null) {
    return undefined;
  } else {
    exhaustiveCheck(violation);
  }
};

type ShippingPlanOrderTableOrder = Pick<
  ScenarioOrder,
  | 'id'
  | 'cargoAmount'
  | 'locationByLoadPortId'
  | 'locationByDischargePortId'
  | 'incomingVoyageCharterRelation'
  | 'assortmentByAssortmentId'
  | 'layday'
  | 'cancelling'
  | 'rta'
  | 'deliveredBefore'
  | 'orderDetailsByOrderId'
> & {
  cargoUnitByCargoUnitId?: null | {
    name?: string | null;
  };
  externalOrderId?: null | string;
  nominatedVessel?: null | string;
  organizationByChartererOrganizationId?: null | {
    name?: string | null;
  };
  isOnAnotherShipment: boolean;
};

function portCallEventTimeSource(
  event: PortCallEvent,
  type: PortCallEventType,
) {
  let label: string;
  let isActualOrEstimate = false;
  let timeSource: string | null;

  switch (type) {
    case PortCallEventType.Arrival:
      label = 'Arrival';
      isActualOrEstimate =
        getPortCallTimeSourceType(event.entry, PortCallEventType.Arrival) ===
        'actual';
      timeSource = getPortCallSourceNameForPortCall(
        event.entry,
        PortCallEventType.Arrival,
      );
      break;
    case PortCallEventType.Berthing:
      label = 'Berthing';
      isActualOrEstimate =
        getPortCallTimeSourceType(event.entry, PortCallEventType.Berthing) ===
        'actual';
      timeSource = getPortCallSourceNameForPortCall(
        event.entry,
        PortCallEventType.Berthing,
      );
      break;
    case PortCallEventType.Departure:
      label = 'Departure';
      isActualOrEstimate =
        getPortCallTimeSourceType(event.entry, PortCallEventType.Departure) ===
        'actual';
      timeSource = getPortCallSourceNameForPortCall(
        event.entry,
        PortCallEventType.Departure,
      );
      break;
    default:
      throw new Error(`Unexpected portcall event type at ${event}`);
  }

  return isActualOrEstimate
    ? `${label} is already sourced from ${timeSource}`
    : '';
}

const portCallTimeSourceReason = (event: PortCallEvent): string => {
  return portCallEventTimeSource(event, event.event);
};

const portCallTimeSourceDurationReason = (
  event: PortCallEvent,
  nextTd: TimeData | undefined,
): string => {
  switch (event.event) {
    case PortCallEventType.Arrival: {
      const arrival = portCallEventTimeSource(event, PortCallEventType.Arrival);
      const berthing = portCallEventTimeSource(
        event,
        PortCallEventType.Berthing,
      );
      return arrival && berthing ? `${arrival} and ${berthing}` : '';
    }
    case PortCallEventType.Berthing: {
      const berthing = portCallEventTimeSource(
        event,
        PortCallEventType.Berthing,
      );
      const hasSofCommenced =
        nextTd && isTimeData(nextTd) && nextTd.event.sofCommenced;
      return hasSofCommenced && berthing
        ? `Next event has commenced value from Statement of facts and ${berthing}`
        : '';
    }
    case PortCallEventType.Departure: {
      return 'Cannot set departure duration, it is only a timestamp';
    }
    default:
      throw new Error(`Unexpected portcall event type at ${event}`);
  }
};

interface OrderTableProps {
  currentUser: User;
  focusedOrderIds: Set<number>;
  orders: ScenarioOrder[];
  onUnassign?: (orderId: number) => void;
  filters: vesselFilterProps;
  setFilters?: (val: vesselFilterProps) => void;
  setVesselDateRange?: (newValue: {
    from: string | undefined;
    to: string | undefined;
  }) => void;
  isReadOnlyScenario?: boolean;
}

const ShippingPlanOrderTable = ({
  currentUser,
  focusedOrderIds,
  orders,
  onUnassign,
  filters,
  setFilters,
  setVesselDateRange,
  isReadOnlyScenario,
}: OrderTableProps): JSX.Element => {
  const { openModal } = useModal();
  const [hoveredOrderId, setHoveredOrderId] = useAtom(hoveredOrderIdAtom);

  const showActions = useMemo(
    () =>
      currentUser.organization?.charterer ||
      typeof currentUser.organization?.parentOrganizationId !== 'number',
    [currentUser.organization],
  );

  const columns: Array<Column<ShippingPlanOrderTableOrder>> = React.useMemo(
    () => [
      {
        Header: 'Order',
        accessor: (order) => {
          return getOrderDisplayName(order);
        },
        Cell: (props: CellProps<ShippingPlanOrderTableOrder, string>) => {
          return (
            <Text>
              {props.value}
              {props.row.original.isOnAnotherShipment && (
                <Badge ml={2}>Not on shipment</Badge>
              )}
            </Text>
          );
        },
        id: 'id',
      },
      {
        Header: 'External Id',
        accessor: (order) => {
          return order.externalOrderId;
        },
        id: 'externalOrderId',
      },
      {
        Header: 'Commodity',
        accessor: (order) => {
          return order.assortmentByAssortmentId?.name;
        },
        id: 'assortment',
      },
      {
        Header: 'Amount',
        accessor: (order) => {
          return parseAmountInfo(order);
        },
        id: 'amount',
      },
      {
        Header: 'From',
        Cell: LocationCell,
        accessor: (order) => {
          return order.locationByLoadPortId;
        },
        id: 'from',
      },
      {
        Header: 'To',
        Cell: LocationCell,
        accessor: (order) => {
          return order.locationByDischargePortId;
        },
        id: 'to',
      },
      {
        Header: 'Freight',
        accessor: (order) => {
          const {
            voyageCharterOrderFreightsByOrderVoyageCharterRelationId: freight,
            voyageCharterByVoyageCharterId,
          } = order.incomingVoyageCharterRelation || {};
          const { freightAmount, freightUnit } = freight?.nodes.at(0) || {};
          const currency = voyageCharterByVoyageCharterId?.currency;
          return freightAmount && freightUnit
            ? `${freightAmount} ${currency ?? ''} ${freightUnit}`
            : ``;
        },
        id: 'freight',
      },
      {
        Header: 'Nominated vessel',
        accessor: (order) => {
          return order.nominatedVessel;
        },
        id: 'nominatedVessel',
      },
      {
        Header: 'Earliest loading',
        accessor: (order) => order.layday ?? null,
        Cell: (
          props: CellProps<ShippingPlanOrderTableOrder, string | null>,
        ) => {
          const order = props.row.original;
          return (
            <TimeField
              value={props.value}
              timeZone={order.locationByLoadPortId?.timezone ?? undefined}
            />
          );
        },
        id: 'layday',
        sortType: (
          rowA: Row<ShippingPlanOrderTableOrder>,
          rowB: Row<ShippingPlanOrderTableOrder>,
        ): number => {
          const aTime = rowA.original.layday
            ? rowA.original.layday
            : Date.now();
          const bTime = rowB.original.layday
            ? rowB.original.layday
            : Date.now();
          return aTime === bTime ? 0 : aTime > bTime ? 1 : -1;
        },
      },
      {
        Header: 'Latest loading',
        accessor: (order) => order.cancelling ?? null,
        Cell: (
          props: CellProps<ShippingPlanOrderTableOrder, string | null>,
        ) => {
          const order = props.row.original;
          return (
            <TimeField
              value={props.value}
              timeZone={order.locationByLoadPortId?.timezone ?? undefined}
            />
          );
        },
        id: 'cancelling',
        sortType: (
          rowA: Row<ShippingPlanOrderTableOrder>,
          rowB: Row<ShippingPlanOrderTableOrder>,
        ): number => {
          const aTime = rowA.original.cancelling
            ? rowA.original.cancelling
            : Date.now();
          const bTime = rowB.original.cancelling
            ? rowB.original.cancelling
            : Date.now();
          return aTime === bTime ? 0 : aTime > bTime ? 1 : -1;
        },
      },
      {
        Header: 'Earliest discharging',
        accessor: (order) => order.rta ?? null,
        Cell: (
          props: CellProps<ShippingPlanOrderTableOrder, string | null>,
        ) => {
          const order = props.row.original;
          return (
            <TimeField
              value={props.value}
              timeZone={order.locationByDischargePortId?.timezone ?? undefined}
            />
          );
        },
        id: 'rta',
        sortType: (
          rowA: Row<ShippingPlanOrderTableOrder>,
          rowB: Row<ShippingPlanOrderTableOrder>,
        ): number => {
          const aTime = rowA.original.rta ? rowA.original.rta : Date.now();
          const bTime = rowB.original.rta ? rowB.original.rta : Date.now();
          return aTime === bTime ? 0 : aTime > bTime ? 1 : -1;
        },
      },
      {
        Header: 'Latest discharging',
        accessor: (order) => order.deliveredBefore ?? null,
        Cell: (
          props: CellProps<ShippingPlanOrderTableOrder, string | null>,
        ) => {
          const order = props.row.original;
          return (
            <TimeField
              value={props.value}
              timeZone={order.locationByDischargePortId?.timezone ?? undefined}
            />
          );
        },
        id: 'latestDelivery',
        sortType: (
          rowA: Row<ShippingPlanOrderTableOrder>,
          rowB: Row<ShippingPlanOrderTableOrder>,
        ): number => {
          const aTime = rowA.original.deliveredBefore
            ? rowA.original.deliveredBefore
            : Date.now();
          const bTime = rowB.original.deliveredBefore
            ? rowB.original.deliveredBefore
            : Date.now();
          return aTime === bTime ? 0 : aTime > bTime ? 1 : -1;
        },
      },
      {
        Header: 'Remarks',
        accessor: (order) =>
          order.orderDetailsByOrderId?.nodes[0]?.extraInfo ?? '',
        Cell: (props: CellProps<ShippingPlanOrderTableOrder, string>) => {
          return <Text maxWidth={200}>{props.value}</Text>;
        },
      },
      {
        Header: 'Charterer',
        accessor: (order) => {
          return order.organizationByChartererOrganizationId?.name ?? '';
        },
        id: 'chartererOrganizationId',
      },
      {
        Header: 'Fixture note',
        accessor: (order) => {
          return order.orderDetailsByOrderId.nodes[0]?.fixtureNote ?? '';
        },
        id: 'fixtureNote',
      },
      {
        Header: 'Actions',
        accessor: () => null,
        Cell: (props: CellProps<ShippingPlanOrderTableOrder, null>) => {
          const order = props.row.original;
          const planningMode = usePlanningMode();

          const incomingVoyageCharterId =
            order.incomingVoyageCharterRelation?.voyageCharterId ?? null;

          const handleViewClick = () => {
            if (
              planningMode === 'voyage_charter' &&
              incomingVoyageCharterId !== null
            ) {
              openModal({
                type: 'EDIT_VOYAGE_CHARTER',
                props: {
                  voyageCharterId: incomingVoyageCharterId,
                },
              });
            } else {
              openModal({
                type: 'VIEW_ORDER',
                props: { orderId: order.id },
              });
            }
          };

          return (
            <Box display="flex">
              {' '}
              {onUnassign && (
                <Button
                  disabled={isReadOnlyScenario}
                  variant="secondary"
                  onPress={() => onUnassign(order.id)}
                >
                  Unassign
                </Button>
              )}
              <Button
                marginLeft={1}
                variant="secondary"
                onPress={handleViewClick}
              >
                View
              </Button>
            </Box>
          );
        },
        id: 'actions',
      },
    ],
    [onUnassign, openModal, isReadOnlyScenario],
  );

  const data = useMemo(
    () =>
      orders.map((o) => ({
        ...o,
        isOnAnotherShipment:
          focusedOrderIds.size > 0 && !focusedOrderIds.has(o.id),
      })),
    [orders, focusedOrderIds],
  );

  const {
    allColumns,
    getTableProps,
    getTableBodyProps,
    getToggleHideAllColumnsProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable<ShippingPlanOrderTableOrder>(
    {
      columns,
      data,
      initialState: {
        hiddenColumns: showActions ? [] : ['actions'],
      },
    },
    useSortBy,
  );

  return (
    <Box position="relative">
      <Box display="flex" justifyContent="space-between">
        <Heading fontSize={4} fontWeight="bold" mb={4}>
          Orders/batches
        </Heading>
        <Box>
          {setFilters && (
            <FilterBarDateRangePicker
              label="Date Range"
              mr={3}
              datePickerProps={{
                start: filters.fromDate,
                end: filters.toDate,
                onChange: (v: [Date, Date]) => {
                  const [fromDate, toDate] = v || [null, null];
                  if (setVesselDateRange) {
                    setVesselDateRange({
                      from: fromDate ? moment(fromDate).format() : undefined,
                      to: toDate ? moment(toDate).format() : undefined,
                    });
                  }

                  setFilters({ fromDate, toDate });
                },
                inputStyle: {
                  border: '1px solid #BDC6D1',
                },
                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              }}
            />
          )}
        </Box>
        <TableColumnsSelector
          variant="secondary"
          allColumns={allColumns}
          getToggleHideAllColumnsProps={getToggleHideAllColumnsProps}
        ></TableColumnsSelector>
      </Box>
      <Box borderColor="neutral.gray_225" borderWidth={1} borderStyle="solid">
        <Table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => (
              // eslint-disable-next-line react/jsx-key
              <TableHeaders {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, i) => (
                  // eslint-disable-next-line react/jsx-key
                  <TableHeader
                    pl={i === 0 ? '10px' : undefined}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                  >
                    {column.render('Header')}{' '}
                    {column.isSorted ? (column.isSortedDesc ? ' ▼' : ' ▲') : ''}
                  </TableHeader>
                ))}
              </TableHeaders>
            ))}
          </thead>
          <tbody {...getTableBodyProps}>
            {rows.map((row, index) => {
              prepareRow(row);
              return (
                // eslint-disable-next-line react/jsx-key
                <TableRow
                  isMuted={index % 2 === 1}
                  onMouseEnter={() => setHoveredOrderId(row.original.id)}
                  onMouseLeave={() => setHoveredOrderId(null)}
                  style={{
                    boxShadow: `inset 5px 0 ${
                      hoveredOrderId === row.original.id
                        ? '#4F88E7'
                        : 'transparent'
                    }`,
                    transition: 'box-shadow .1s ease-out',
                    paddingLeft: '10px',
                    opacity: row.original.isOnAnotherShipment ? 0.5 : 1,
                  }}
                  {...row.getRowProps()}
                >
                  {row.cells.map((cell, i) => (
                    <OrderTableCellRenderer key={i} cell={cell} />
                  ))}
                </TableRow>
              );
            })}
          </tbody>
        </Table>
      </Box>
    </Box>
  );
};

const OrderTableCellRenderer = memo(
  ({ cell }: { cell: Cell<ShippingPlanOrderTableOrder> }): JSX.Element => (
    <TableCell {...cell.getCellProps()}>{cell.render('Cell')}</TableCell>
  ),
  (prevProps, nextProps) => prevProps.cell === nextProps.cell,
);

const portCallEventTypeToLabel = {
  [PortCallEventType.Arrival]: 'ETA',
  [PortCallEventType.Berthing]: 'ETB',
  [PortCallEventType.Departure]: 'ETD',
};

const singleEventTypeToLabel = singleEventTypes.map(({ label }) => label);

const singleEventTypeToColor = {
  [SingleEventType.MANEUVERING]: 'voyageEvents.event',
  [SingleEventType.BUNKERING]: 'voyageEvents.event',
  [SingleEventType.CLEANING]: 'voyageEvents.event',
  [SingleEventType.DRIFTING]: 'voyageEvents.event',
  [SingleEventType.CANAL_TRANSIT]: 'voyageEvents.event',
  [SingleEventType.PASSING]: 'voyageEvents.event',
  [SingleEventType.REPAIR]: 'voyageEvents.event',
  [SingleEventType.WAITING]: 'voyageEvents.idle',
  [SingleEventType.CONGESTION]: 'voyageEvents.event',
  [SingleEventType.OTHER]: 'voyageEvents.event',
};

const portCallEventTypeToColor = {
  [PortCallEventType.Arrival]: 'voyageEvents.portCall',
  [PortCallEventType.Berthing]: 'voyageEvents.portCall',
  [PortCallEventType.Departure]: 'voyageEvents.portCall',
};

const getSingleEventTypeLabel = (e: SingleEventType): string =>
  singleEventTypeToLabel[e];
const getPortCallEventTypeLabel = (e: PortCallEventType): string =>
  portCallEventTypeToLabel[e];
const getSingleEventActivityColor = (e: SingleEventType): string =>
  singleEventTypeToColor[e];
const getPortCallEventActivityColor = (e: PortCallEventType): string =>
  portCallEventTypeToColor[e];

const AddEventDropdown = ({
  onCreateSingleEvent,
  locationId,
  locationName,
  locationTimeZone,
  index,
  start,
  disabled,
  isReadOnlyScenario,
}: {
  onCreateSingleEvent: (
    type: SingleEventType,
    locationId: number,
    locationName: string,
    locationTimeZone: string | null,
    index: number,
    start: number,
    duration?: number,
  ) => void;
  locationId: number;
  locationName: string;
  locationTimeZone: string | null;
  index: number;
  start: number;
  disabled?: boolean;
  isReadOnlyScenario?: boolean;
}): JSX.Element => {
  return (
    <DropdownButton
      label="+"
      variant="secondary"
      disabled={disabled || isReadOnlyScenario}
    >
      <Menu hasShadow>
        {singleEventTypes.map(({ type, label }) => {
          return (
            <MenuItem
              key={label}
              label={label}
              onPress={() => {
                onCreateSingleEvent(
                  type,
                  locationId,
                  locationName,
                  locationTimeZone,
                  index,
                  start,
                );
              }}
            />
          );
        })}
      </Menu>
    </DropdownButton>
  );
};

const ActivityIndicator = styled(View)`
  border: 1px solid #000;
  border-radius: 10px;
  margin-right: 5px;
  flex: 0 0 auto;
  height: 16px;
  width: 16px;
`;

interface StartTimePickerProps extends DatePickerProps {
  // Date is coming from SOF
  isActual: boolean;
  startOverride: number | null;
  onReset?: () => void;
  /** Time violation display text. Presence also indicates an error highlight on coloring. */
  timeViolation: TimeViolationType | null;
  onViolationClick: null | ((e: React.MouseEvent) => void);
}

const StartTimePicker = ({
  isActual,
  timeViolation,
  startOverride,
  onViolationClick,
  onReset,
  ...restProps
}: StartTimePickerProps) => {
  const isOverride = startOverride !== null;
  const statusIndicatorColor =
    timeViolation === 'override_conflict' ||
    timeViolation === 'physical_conflict'
      ? 'voyageEvents.violation'
      : isActual
        ? 'blue.blue_400'
        : isOverride
          ? 'black'
          : undefined;

  return (
    <Box position="relative">
      <DatePicker
        showTime
        showTimeInput
        hideTimePickerTypes
        defaultTimePickerType="CUSTOM"
        isClearable={false}
        {...restProps}
      >
        {onReset && (
          <Box width={1} mt={3}>
            <Button
              disabled={!isOverride}
              onPress={onReset}
              width={1}
              variant="secondary"
            >
              Reset
            </Button>
          </Box>
        )}
      </DatePicker>
      {(isOverride || isActual || timeViolation) && (
        <Box
          position="absolute"
          bottom={0}
          left={0}
          right={0}
          height="3px"
          bg={statusIndicatorColor}
        />
      )}
      {timeViolation && (
        <Box
          position="absolute"
          right={0}
          top={0}
          padding={3}
          title={generateViolationTitleText(
            timeViolation,
            startOverride !== null
              ? { value: startOverride, timeZone: restProps.timeZone }
              : null,
          )}
          style={{
            cursor: onViolationClick ? 'pointer' : undefined,
          }}
          onClick={(e) => {
            onViolationClick?.(e);
            e.stopPropagation();
          }}
        >
          <AlertIcon />
        </Box>
      )}
    </Box>
  );
};

interface PortRotationTableProps {
  isEditable: boolean;
  isFocusedShipmentShared: boolean;
  onEventChange: <T extends Events = Events>(
    event: T,
    change: Partial<T>,
  ) => void;
  onSwap?: ScenarioTimelineProps['onSwapScheduleEntry'];
  onSwapPortCall: ScenarioTimelineProps['onSwapPortCall'];
  onOptimizeVoyageSpeed?: (params: {
    timelineId: EventTimeline['id'];
    portCallsToOptimize: Set<ClientId>;
  }) => void;
  onDelete: (index: number) => void;
  eventsAndLegs: EventsAndLegs;
  timeData: TimeData[];
  totals: TimeDataTotals;
  inputs: TimeDataInputs | undefined;
  eventTimeline: EventTimeline;
  onCreateSingleEvent: (
    type: SingleEventType,
    locationId: number,
    locationName: string,
    locationTimeZone: string | null,
    index: number,
    start: number,
    duration?: number,
  ) => void;
  hasMoreEvents: [boolean, boolean];
  onShowMoreEvents: (
    direction: 'before' | 'after' | 'beforeAll' | 'afterAll' | 'prev' | 'next',
  ) => void;
  currentUser: User;
  prevVoyageIndex: number;
  nextVoyageIndex: number;
  voyageGroups: VoyageGroup[] | EventsWithPortCalls[];
  berthPredictions: DefaultMap<
    ClientId,
    Map<'L' | 'D', CachedBerthPrediction | null | undefined>
  >;
  isBerthPredictionAvailable: boolean;
  isChannelUsageToggleAvailable: boolean;
  vessel: ShippingPlan_ShipFragment | undefined;
  voyageConsumptionData: Map<
    number,
    Pick<VoyageCalculation, 'fuelConsumptionBreakdown'>
  >;
  scheduleContext?: ScheduleDataContext;
  shipment: ScenarioShipment | null;
  isReadOnlyScenario: boolean;
}

function getBerthPrediction(
  clientId: ClientId | null,
  berthPredictions: DefaultMap<
    ClientId,
    Map<'L' | 'D', CachedBerthPrediction | null | undefined>
  >,
  isLoad: boolean,
) {
  if (clientId) {
    return berthPredictions.get(clientId).get(isLoad ? 'L' : 'D');
  }
  return null;
}

const findCurrentArrivalEventAndIndex = (
  eventsAndLegs: EventsAndLegs,
  fromIndex: number,
): [PortCallArrivalEvent, number] | [null, null] => {
  for (let i = fromIndex; i >= 0; --i) {
    const td = eventsAndLegs[i];
    if (isTimeData(td) && isPortCallArrivalEvent(td.event)) {
      return [td.event, i];
    }
  }
  return [null, null];
};

const findNextSourcedEventIndex = (
  eventsAndLegs: EventsAndLegs,
  fromIndex: number,
): number => {
  for (let i = fromIndex + 1, l = eventsAndLegs.length; i < l; i++) {
    const td = eventsAndLegs[i];
    if (
      isTimeData(td) &&
      isPortCallEvent(td.event) &&
      !!portCallTimeSourceReason(td.event)
    ) {
      return i;
    }
  }
  return -1;
};

const portCallDurationOverriddenTitle =
  'Total duration has been set for this port call';

const FuelTypes: (keyof Pick<FuelSummaries, 'HFO' | 'LSFO' | 'MGO' | 'LNG'>)[] =
  ['HFO', 'LSFO', 'MGO', 'LNG'];

const FuelCostEstimate = React.memo(function FuelCostEstimate(props: {
  voyageConsumptionData: Map<
    number,
    Pick<VoyageCalculation, 'fuelConsumptionBreakdown'>
  >;
  index: number;
}) {
  const value = props.voyageConsumptionData.get(props.index)
    ?.fuelConsumptionBreakdown?.value;
  if (value == null) {
    return null;
  }
  return (
    <>
      {FuelTypes.map((fuelType) => {
        const data = value[fuelType];
        if (!data) {
          return null;
        }
        const { consumption, cost } = data;
        return (
          <p key={fuelType}>
            {fuelType}
            <br /> consumption {Math.round(consumption / 100) / 10} t
            <br /> cost {Math.round(cost)} USD
          </p>
        );
      }).filter(Boolean)}
    </>
  );
});

const EditingPortRotationRow = ({
  rowIndex,
  isMuted,
  td,
  setEditingRowIndex,
  onCreateSingleEvent,
  eventTimeline,
  eventsAndLegs,
  timeData,
  isFocusedShipmentShared,
}: {
  rowIndex: number;
  isMuted: boolean;
  td: Leg & { key: string };
  setEditingRowIndex: (idx: number) => void;
  eventTimeline: EventTimeline;
  eventsAndLegs: EventsAndLegs;
  timeData: TimeData[];
} & Pick<
  PortRotationTableProps,
  'onCreateSingleEvent' | 'isFocusedShipmentShared'
>) => {
  const [selectedType, setSelectedType] = useState(0);
  const [selectedLocation, setSelectedLocation] = useState<
    LocationSelectValue | undefined
  >(undefined);
  const [duration, setDuration] = useState(hToMs(1));

  const prev = 'from' in td ? td.from.id : null;
  const next = 'to' in td ? td.to.id : null;
  const current = selectedLocation?.value ?? null;

  const { noGoZones } = useDistances();
  const distanceTo = useSingleDistance(prev, current, false, noGoZones) ?? 0;
  const distanceFrom = useSingleDistance(current, next, false, noGoZones) ?? 0;

  const typeOptions = singleEventTypes.map((t) => ({
    id: t.type,
    label: capitalize(t.label),
  }));

  const speed = td.distance / td.hoursAtSea;

  const start = td.start;

  const hoursAtSeaTo = distanceTo / speed;

  const calculatedDurationTo = hToMs(hoursAtSeaTo);

  const endTo = start + calculatedDurationTo;

  const location = selectedLocation?.location as
    | ScenarioLocation
    | undefined
    | null;

  const startFrom = endTo + duration;

  const hoursAtSeaFrom = distanceFrom / speed;

  const calculatedDurationFrom = hToMs(hoursAtSeaFrom);

  const endFrom = startFrom + calculatedDurationFrom;

  const eventIndex = rowIndex;

  const legTo: Leg = {
    from: td.from,
    start,
    end: endTo,
    to: location ?? td.from,
    category: EventCategory.Leg,
    index: eventIndex,
    event: td.event,
    hoursAtSea: hoursAtSeaTo,
    channelDurationOverhead: null,
    distance: distanceTo,
    avoidsChannels: false,
    calculatedDuration: calculatedDurationTo,
    seaDurationOverride: null,
    laden: td.laden,
    key: '',
    usedChannels: [],
  };
  const rowDataTo: {
    eventIndex: number;
    event: EventOrLeg;
    location: ScenarioLocation;
  } = {
    eventIndex,
    event: legTo,
    location: legTo.from,
  };

  const legFrom: Leg = {
    from: location ?? td.to,
    start: startFrom,
    end: endFrom,
    to: td.to,
    category: EventCategory.Leg,
    index: eventIndex,
    event: td.event,
    hoursAtSea: hoursAtSeaFrom,
    channelDurationOverhead: null,
    distance: distanceFrom,
    avoidsChannels: false,
    calculatedDuration: calculatedDurationFrom,
    seaDurationOverride: null,
    laden: td.laden,
    key: '',
    usedChannels: [],
  };
  const rowDataFrom: {
    eventIndex: number;
    event: EventOrLeg;
    location: ScenarioLocation;
  } = {
    eventIndex,
    event: legFrom,
    location: legFrom.from,
  };

  const skipIds = new Set([td.from.id, td.to.id]);

  const selectedDate = useMemo(() => moment(endTo).toDate(), [endTo]);

  return (
    <>
      {legTo && rowDataTo && (
        <PortRotationRow
          td={legTo}
          eventIndex={eventIndex}
          rowIndex={rowIndex}
          isMuted={isMuted}
          rowData={[rowDataTo]}
          isEditable={true}
          showActions={true}
          showExtended={false}
          isPlanningPage={true}
          isChannelUsageToggleAvailable={false}
          onCreateSingleEvent={noop}
          setEditingRowIndex={noop}
          onViolationMenu={noop}
          onSwapPortCall={noop}
          onEventChange={noop}
          onOptimizeVoyageSpeed={noop}
          onDelete={noop}
          onSwap={noop}
          voyageConsumptionData={new Map()}
          eventsAndLegs={eventsAndLegs}
          timeData={timeData}
          eventTimeline={eventTimeline}
          hideAddSingleEvent={true}
          isFocusedShipmentShared={isFocusedShipmentShared}
        />
      )}
      <TableRow isMuted={!isMuted}>
        <TableCell></TableCell>
        <TableCell>
          <Box width={120}>
            <Select
              name="type"
              value={selectedType}
              options={typeOptions}
              enablePlaceholder
              required
              onChange={(e) => setSelectedType(+e.target.value)}
            />
          </Box>
        </TableCell>
        <TableCell>
          <Box width={120}>
            <LocationSelect
              value={selectedLocation}
              onChange={setSelectedLocation}
              skipIds={skipIds}
            />
          </Box>
        </TableCell>
        <TableCell></TableCell>
        <TableCell></TableCell>
        <TableCell></TableCell>
        <TableCell></TableCell>
        <TableCell>
          <Box width={200} mr={2}>
            <StartTimePicker
              disabled={true}
              selected={selectedDate}
              timeZone={location?.timezone || undefined}
              isActual={false}
              startOverride={null}
              timeViolation={null}
              onViolationClick={null}
              onChange={noop}
            />
          </Box>
        </TableCell>
        <TableCell />
        <TableCell />
        <TableCell>
          <DurationInput
            overrideDuration={duration}
            defaultDuration={null}
            onUpdateDuration={(v) => setDuration(v ?? 0)}
          />
        </TableCell>
        <TableCell></TableCell>
        <TableCell></TableCell>
        <TableCell style={{ whiteSpace: 'nowrap' }}>
          <DeleteButton onPress={() => setEditingRowIndex(-1)} />
          <Button
            ml={3}
            variant="secondary"
            disabled={!selectedLocation}
            onPress={() => {
              onCreateSingleEvent(
                selectedType,
                selectedLocation?.value ?? 0,
                selectedLocation?.label ?? '',
                selectedLocation?.location?.timezone ?? null,
                td.index || -1,
                start ?? 0,
                duration,
              );
              setEditingRowIndex(-1);
            }}
          >
            Create
          </Button>
        </TableCell>
      </TableRow>
      {legFrom && rowDataFrom && (
        <PortRotationRow
          td={legFrom}
          isFocusedShipmentShared={isFocusedShipmentShared}
          eventIndex={eventIndex}
          rowIndex={rowIndex}
          isMuted={isMuted}
          rowData={[rowDataFrom]}
          isEditable={true}
          showActions={true}
          showExtended={false}
          isPlanningPage={true}
          isChannelUsageToggleAvailable={false}
          onCreateSingleEvent={noop}
          setEditingRowIndex={noop}
          onViolationMenu={noop}
          onSwapPortCall={noop}
          onEventChange={noop}
          onOptimizeVoyageSpeed={noop}
          onDelete={noop}
          onSwap={noop}
          voyageConsumptionData={new Map()}
          eventsAndLegs={eventsAndLegs}
          timeData={timeData}
          eventTimeline={eventTimeline}
          hideAddSingleEvent={true}
        />
      )}
    </>
  );
};

const dayToName = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun', 'Holiday'];

function formatLoadingRateSource(source: LoadingRateSource | undefined | null) {
  switch (source) {
    case 'assortment':
    case 'coa':
    case 'order':
    case 'port':
    case 'vessel':
    case 'voyage_charter':
    case 'port_commodity':
      return loadingRateSourceToLabel[source];
    case null:
    case undefined: {
      return 'Not available';
    }
    default:
      exhaustiveCheck(source);
  }
}

const PortRotationRow = React.memo(
  ({
    td,
    rowData,
    rowIndex,
    eventIndex,
    isMuted,
    onEventChange,
    eventsAndLegs,
    onDelete,
    onSwap,
    onSwapPortCall,
    onOptimizeVoyageSpeed,
    isEditable,
    timeData,
    onCreateSingleEvent,
    eventTimeline,
    showActions,
    showExtended,
    isPlanningPage,
    voyageConsumptionData,
    onViolationMenu,
    setEditingRowIndex,
    isChannelUsageToggleAvailable,
    hideAddSingleEvent,
    isFocusedShipmentShared,
    isReadOnlyScenario,
  }: {
    td: TimeData | Leg | WaitEvent;
    rowData: ShippingPlanRowData[];
    rowIndex: number;
    eventIndex: number;
    isMuted: boolean;
    showActions: boolean;
    showExtended: boolean;
    isPlanningPage: boolean;
    onViolationMenu: (event: EventOrLeg, e: React.MouseEvent) => void;
    setEditingRowIndex: (idx: number) => void;
    hideAddSingleEvent?: boolean;
    isReadOnlyScenario?: boolean;
  } & Pick<
    PortRotationTableProps,
    | 'onEventChange'
    | 'eventsAndLegs'
    | 'onDelete'
    | 'onSwap'
    | 'onSwapPortCall'
    | 'onOptimizeVoyageSpeed'
    | 'isEditable'
    | 'timeData'
    | 'onCreateSingleEvent'
    | 'eventTimeline'
    | 'isChannelUsageToggleAvailable'
    | 'voyageConsumptionData'
    | 'isFocusedShipmentShared'
  >) => {
    const [estimatedDuration, actualDuration] = getTimedataDurations(td);
    const [violationBasis] = useViolationIndicatorBasisState();

    const estimatedDurationH = msToH(estimatedDuration);
    const actualDurationH = msToH(actualDuration);

    const { openModal } = useModal();

    const [hoveredOrderId, setHoveredOrderId] = useAtom(hoveredOrderIdAtom);

    const isSpotTimeline = eventTimeline.type === 'spot';
    const isCoaTimeline = eventTimeline.type === 'coa';

    const durationNotEditableReason =
      isTimeData(td) &&
      (td.event.sofDuration ? 'Duration comes from SOF' : null);

    const canHaveSingleEvents = eventTimeline.type === 'tc';

    const durationsEditable = isEditable && !durationNotEditableReason;
    const hasSofCommenced = isTimeData(td) && td.event.sofCommenced;

    const { currentArrivalIndex, portCallEvents } = useMemo(() => {
      const [currentArrival, currentArrivalIndex] =
        findCurrentArrivalEventAndIndex(eventsAndLegs, eventIndex);

      const portCallEvents =
        currentArrivalIndex !== null && currentArrival
          ? takeWhileFrom(
              eventsAndLegs,
              currentArrivalIndex,
              (td) =>
                (!isTimeData(td) ||
                  td.event.portCallId === currentArrival.portCallId) &&
                !isLeg(td),
            )
          : [];

      return {
        currentArrivalIndex,
        portCallEvents,
      };
    }, [eventIndex, eventsAndLegs]);

    const { hasSourcedEventInFuture, totalBuffer } = useMemo(() => {
      const nextSourcedEventIndex = findNextSourcedEventIndex(
        eventsAndLegs,
        eventIndex,
      );

      const hasSourcedEventInFuture = nextSourcedEventIndex !== -1;

      const eventBeforeNextSourced = hasSourcedEventInFuture
        ? eventsAndLegs[nextSourcedEventIndex - 1]
        : null;

      const [, actualDurationBeforeNextSourced] =
        eventBeforeNextSourced && nextSourcedEventIndex !== eventIndex + 1
          ? getTimedataDurations(eventBeforeNextSourced)
          : [0, 0];

      const totalBuffer =
        actualDuration + actualDurationBeforeNextSourced - 500;

      return {
        hasSourcedEventInFuture,
        totalBuffer,
      };
    }, [actualDuration, eventIndex, eventsAndLegs]);

    const portCallShipmentIds = useMemo(
      () =>
        uniq(
          portCallEvents.flatMap((td) =>
            isTimeData(td) && isCargoEventTimeData(td)
              ? [td.event.order.shipmentId]
              : [],
          ),
        ),
      [portCallEvents],
    );

    const voyageHasAnyTankAllocations = useMemo(
      () =>
        timeData.some(
          (td) =>
            isTCOrderEvent(td.event) &&
            td.event.cargoAllocations.some((a) => +a.amount > 0),
        ),
      [timeData],
    );

    const timesEditable =
      isEditable &&
      !hasSofCommenced &&
      !((isSpotTimeline || isCoaTimeline) && 'from' in td);

    const timesNotEditableReason = timesEditable
      ? ''
      : hasSofCommenced
        ? 'Time comes from SOF'
        : 'Time cannot be edited';

    const portcallEvent = !('isGenerated' in td || 'from' in td)
      ? td.event
      : null;

    const isPortCallAvailable = portcallEvent && isPortCallEvent(portcallEvent);
    const portCall = isPortCallAvailable ? portcallEvent?.entry : null;

    const sourceType = useMemo(
      () => ({
        [PortCallEventType.Arrival]: portCall
          ? getPortCallTimeSourceType(portCall, PortCallEventType.Arrival)
          : '',
        [PortCallEventType.Berthing]: portCall
          ? getPortCallTimeSourceType(portCall, PortCallEventType.Berthing)
          : '',
        [PortCallEventType.Departure]: portCall
          ? getPortCallTimeSourceType(portCall, PortCallEventType.Departure)
          : '',
      }),
      [portCall],
    );

    const { start, end } = td;
    const isPast = Date.now() - (start ?? 0) > 0;
    const selectedDate = useMemo(
      () => (start ? new Date(start) : null),
      [start],
    );

    // Vessel overdraft warning
    const guardedTimeData = isTimeData(td) ? td : undefined;
    const overDraftWarning = useMemo(
      () =>
        guardedTimeData ? getDraftWarningAtEvent(guardedTimeData) : undefined,
      [guardedTimeData],
    );

    // Laden/Ballast row
    if ('from' in td) {
      const { index, distance, channelDurationOverhead } = td;
      // The next non-implicit event
      const nextTd = find(eventsAndLegs, isTimeData, eventIndex + 1);
      const nextEvent = nextTd?.event;

      const prevTd = get(eventsAndLegs, eventIndex - 1);
      const departureEvent =
        prevTd && isTimeData(prevTd) && isPortCallEvent(prevTd.event)
          ? prevTd.event
          : null;

      const departureTimeDefined =
        departureEvent && portCallTimeSourceReason(departureEvent);

      const arrivalTimeDefined =
        nextEvent &&
        isPortCallEvent(nextEvent) &&
        portCallTimeSourceReason(nextEvent);

      const durationIsDerived =
        departureTimeDefined && arrivalTimeDefined
          ? `${departureTimeDefined} and ${arrivalTimeDefined}`
          : '';

      return (
        <TableRow isMuted={isMuted}>
          {isEditable && (
            <TableCell display="flex">
              {!hideAddSingleEvent && canHaveSingleEvents && rowIndex !== 0 && (
                <Button
                  width="100%"
                  variant="secondary"
                  onPress={() => setEditingRowIndex(rowIndex)}
                  disabled={isReadOnlyScenario}
                >
                  Add port call
                </Button>
              )}
            </TableCell>
          )}
          <TableCell>
            <View flexDirection="row">
              <ActivityIndicator bg="voyageEvents.voyage" />
              <Text>{td.laden ? 'Laden' : 'Ballast'}</Text>
            </View>
          </TableCell>
          <TableCell>⇩</TableCell>
          <TableCell>
            {td.usedChannels.length
              ? `Channels used: ${td.usedChannels.map(formatChannelName).join(', ')}`
              : ''}
          </TableCell>
          <TableCell></TableCell>
          <TableCell
            title={
              channelDurationOverhead
                ? `Channel duration overhead: ${oneDecimal(
                    channelDurationOverhead,
                  )}h`
                : undefined
            }
          >
            {channelDurationOverhead
              ? `${oneDecimal(channelDurationOverhead)}h + `
              : ''}
            {oneDecimal(
              distance / (actualDurationH - (channelDurationOverhead ?? 0)),
            )}{' '}
            kn
          </TableCell>
          <TableCell>{oneDecimal(distance)}</TableCell>
          <TableCell>
            <Box display="flex" alignItems="center">
              <Box width={200} mr={2}>
                <StartTimePicker
                  disabled={
                    !isEditable ||
                    !timesEditable ||
                    !!departureTimeDefined ||
                    eventIndex < 1 ||
                    hideAddSingleEvent ||
                    isReadOnlyScenario
                  }
                  title={departureTimeDefined || timesNotEditableReason}
                  isActual={false}
                  startOverride={null}
                  timeViolation={null}
                  onViolationClick={null}
                  minDate={
                    eventIndex > 0
                      ? new Date(
                          eventsAndLegs[eventIndex - 1].start +
                            minimumEventDuration,
                        )
                      : undefined
                  }
                  onChange={(newStart: Date) => {
                    const prevIndex = index - 1;
                    const { event: prevEvent } = timeData[prevIndex];
                    const updatedPrevDuration = +newStart - prevEvent.start;
                    if (isPortCallEvent(prevEvent)) {
                      onEventChange<PortCallEvent>(prevEvent, {
                        duration: updatedPrevDuration,
                      });
                    } else if (isSingleEvent(prevEvent)) {
                      onEventChange<SingleEvent>(prevEvent, {
                        duration: updatedPrevDuration,
                      });
                    } else if (isTCOrderEvent(prevEvent)) {
                      onEventChange<TCOrderEvent>(prevEvent, {
                        durationOverride: updatedPrevDuration,
                      });
                    } else {
                      console.warn(
                        `Unhandled prev event type in updating leg duration. Maybe impossible to change its duration.`,
                      );
                    }
                  }}
                  selected={selectedDate}
                  timeZone={td.from.timezone ?? undefined}
                  maxDate={
                    hasSourcedEventInFuture && start
                      ? new Date(start + totalBuffer)
                      : null
                  }
                />
              </Box>
            </Box>
          </TableCell>
          <TableCell>
            {nextEvent && isPortCallArrivalEvent(nextEvent) && (
              <>
                <NumericInput
                  defaultValue={nextEvent.weatherFactor || 0}
                  overrideValue={nextEvent.weatherFactor || 0}
                  onUpdate={(updatedValue) => {
                    onEventChange(nextEvent, {
                      weatherFactor: updatedValue,
                      seaDurationOverride: updatedValue
                        ? Math.round(
                            estimatedDuration * (1 + updatedValue / 100),
                          )
                        : null,
                    });
                  }}
                  max={100}
                  min={0}
                />
                <Text>%</Text>
              </>
            )}
          </TableCell>
          <TableCell>{oneDecimal(estimatedDurationH)}</TableCell>
          <TableCell>
            {/* OffHire start & end times are fixed so changing the duration of the previous trip wouldn't do anything */}
            {nextTd &&
              'event' in nextTd &&
              nextTd.event.type !== CoreEventType.OffHire && (
                <DurationInput
                  overrideDuration={nextTd.event.seaDurationOverride}
                  defaultDuration={actualDuration}
                  onUpdateDuration={(updatedFullDuration) => {
                    if (!nextTd) {
                      return;
                    }
                    const seaDurationOverride =
                      updatedFullDuration === estimatedDuration ||
                      updatedFullDuration === null
                        ? null
                        : updatedFullDuration;

                    onEventChange(nextTd.event, {
                      seaDurationOverride,
                    });
                  }}
                  disabled={
                    !durationsEditable ||
                    hideAddSingleEvent ||
                    !!durationIsDerived ||
                    isReadOnlyScenario
                  }
                  title={durationNotEditableReason || durationIsDerived || ''}
                  tabIndex={10000 + rowIndex}
                  max={hasSourcedEventInFuture ? msToH(totalBuffer) : undefined}
                />
              )}
          </TableCell>
          <TableCell />
          <TableCell style={{ whiteSpace: 'nowrap' }}>
            {isChannelUsageToggleAvailable &&
              nextEvent &&
              isPortCallArrivalEvent(nextEvent) && (
                <OptimisticCheckbox
                  checked={!nextEvent.legAvoidsChannels}
                  label="use channels"
                  isSingleLine={true}
                  onChange={(newValue) =>
                    onEventChange(nextEvent, {
                      legAvoidsChannels: !newValue,
                    })
                  }
                  disabled={isReadOnlyScenario}
                />
              )}
          </TableCell>
          {showActions && (
            <TableCell>
              <View display="flex" alignItems="center" style={{ gap: '8px' }}>
                <FuelCostEstimate
                  voyageConsumptionData={voyageConsumptionData}
                  index={td.index}
                />
                {onOptimizeVoyageSpeed && (
                  <Button
                    variant="secondary"
                    size="small"
                    disabled={isReadOnlyScenario || !nextTd?.event?.portCallId}
                    title="Optimize speed for this leg"
                    onPress={() => {
                      if (nextTd?.event?.portCallId) {
                        onOptimizeVoyageSpeed({
                          timelineId: eventTimeline.id,
                          portCallsToOptimize: new Set([
                            nextTd.event.portCallId,
                          ]),
                        });
                      }
                    }}
                  >
                    Optimize
                  </Button>
                )}
              </View>
            </TableCell>
          )}
          {showExtended && <TableCell colSpan={10} />}
        </TableRow>
      );
      // Idle row
    } else if ('isGenerated' in td) {
      const { eventType, locationName } = td;
      const [estimatedDuration, actualDuration] = getTimedataDurations(td);
      const estimatedDurationH = msToH(estimatedDuration),
        actualDurationH = msToH(actualDuration);
      const nextTd = find(eventsAndLegs, isTimeData, eventIndex + 1);
      const sofOverrides =
        nextTd &&
        isPortCallEventTimeData(nextTd) &&
        nextTd.event.portCall &&
        getSofAndNoticeOverridesFromPortCall(nextTd.event.portCall);

      /** Cannot delete idle row when previous and next row have fixed times from SoF */
      const isIdleDeletable =
        !sofOverrides ||
        !(
          isPortCallDepartureEventTimeData(nextTd) &&
          portCallTimeSourceReason(nextTd.event) &&
          sofOverrides.duration !== null
        );

      return (
        <TableRow isMuted={isMuted}>
          {isEditable && <TableCell />}
          <TableCell>
            <View flexDirection="row">
              <ActivityIndicator
                bg={getSingleEventActivityColor(SingleEventType.WAITING)}
              />
              <Text>{capitalize(ImplicitEventType[eventType])}</Text>
            </View>
          </TableCell>
          <TableCell>{locationName}</TableCell>
          <TableCell></TableCell>
          <TableCell></TableCell>
          <TableCell></TableCell>
          <TableCell></TableCell>
          <TableCell>
            <Box display="inline-flex" flexDirection="row">
              <View width={200}>
                <TimeField
                  value={start}
                  timeZone={td.locationTimeZone ?? undefined}
                />
              </View>
            </Box>
          </TableCell>
          <TableCell>{oneDecimal(estimatedDurationH)} </TableCell>
          <TableCell>{oneDecimal(actualDurationH)}</TableCell>
          <TableCell></TableCell>
          <TableCell style={{ whiteSpace: 'nowrap' }}></TableCell>
          {showActions && (
            <TableCell
              style={{ whiteSpace: 'nowrap' }}
              data-testid="delete-idle-row"
            >
              {isEditable && isIdleDeletable && (
                <DeleteButton
                  onPress={() => {
                    const idleRowIndex = rowData.findIndex(
                      (rd) => 'event' in rd && rd.eventIndex === eventIndex,
                    );

                    const prevRow = rowData[idleRowIndex - 1];
                    // Need to target next row if the prevRow is a Leg row
                    const nextRow = rowData[idleRowIndex + 1];

                    // Total Loading/Discharging row
                    if ('data' in prevRow) {
                      const {
                        data: { duration, durations, onChangeDuration },
                      } = prevRow;

                      onChangeDuration(
                        (duration ??
                          msInH *
                            durations.reduce(
                              (prevValue, curValue) => prevValue + curValue,
                              0,
                            )) + actualDuration,
                      );

                      // Leg row
                    } else if (
                      isLeg(prevRow.event) &&
                      'event' in nextRow &&
                      isTimeData(nextRow.event)
                    ) {
                      const seaDurationOverride =
                        (nextRow.event.event.seaDurationOverride ??
                          msInH * nextRow.event.hoursAtSea) + actualDuration;

                      onEventChange(nextRow.event.event, {
                        seaDurationOverride,
                      });

                      // All the other rows
                    } else if ('category' in prevRow.event.event) {
                      const { event } = prevRow.event;
                      onEventChange(event, {
                        duration: event.duration + actualDuration,
                      });
                    } else {
                      console.warn(`Unhandled deleting idle row`);
                    }
                  }}
                />
              )}
            </TableCell>
          )}
        </TableRow>
      );
    }

    const {
      amount,
      physicalTimeConflict,
      startOverrideConflict,
      event,
      index,
      isFirstEvent,
      isLastEvent,
      hasMiddle,
      work,
    } = td;

    const cancellingOrDeliveryViolation =
      violationBasis === 'eta'
        ? td.cancellingOrDeliveryViolationVsEta
        : td.cancellingOrDeliveryViolationVsOperations;

    const timeViolation =
      getTimeViolationType(
        cancellingOrDeliveryViolation,
        physicalTimeConflict,
        startOverrideConflict,
      ) ?? null;

    const handleViolationClick = () =>
      timeViolation && timeViolation !== 'order_reqs'
        ? (e: React.MouseEvent) => {
            onViolationMenu(td, e);
          }
        : null;

    switch (event.type) {
      // Can mean either SingleEvent or PortCallEvent
      case CoreEventType.Single: {
        const {
          duration,
          locationName,
          locationId,
          locationTimeZone,
          startOverride,
        } = event;

        const nextTd = find(eventsAndLegs, isTimeData, eventIndex + 1);

        const portCallDurationReason = isPortCallEvent(event)
          ? portCallTimeSourceDurationReason(event, nextTd)
          : '';

        const berthingTd = currentArrivalIndex
          ? find(
              eventsAndLegs,
              (td) => isTimeData(td) && isPortCallBerthingEventTimeData(td),
              currentArrivalIndex + 1,
            )
          : null;

        const minStartDate =
          isPortCallDepartureEvent(event) && berthingTd
            ? new Date(berthingTd.start + 500)
            : eventIndex > 0
              ? new Date(eventsAndLegs[eventIndex - 1].start + 500)
              : new Date(0);

        return (
          <>
            <TableRow
              data-testid={`shipping-plan-row-${locationName}-${event.event}`}
              isMuted={isMuted}
            >
              {isEditable && (
                <TableCell>
                  {onSwap && !isPortCallEventTimeData(td) && (
                    <View flexDirection="row">
                      <Button
                        width={32}
                        mr={1}
                        disabled={!!td.event.skipOnSwap || isReadOnlyScenario}
                        onPress={() =>
                          onSwap(
                            eventTimeline.id,
                            index,
                            SwapDirection.Left,
                            td,
                          )
                        }
                      >
                        ↑
                      </Button>
                      <Button
                        width={32}
                        mr={2}
                        disabled={!!td.event.skipOnSwap || isReadOnlyScenario}
                        onPress={() =>
                          onSwap(
                            eventTimeline.id,
                            index,
                            SwapDirection.Right,
                            td,
                          )
                        }
                      >
                        ↓
                      </Button>
                      {canHaveSingleEvents && (
                        <AddEventDropdown
                          {...{
                            onCreateSingleEvent,
                            locationId,
                            locationName,
                            locationTimeZone,
                            index,
                            start: start - 1,
                            isReadOnlyScenario,
                          }}
                        />
                      )}
                    </View>
                  )}
                  {onSwapPortCall && isPortCallEventTimeData(td) && (
                    <View flexDirection="row">
                      <Button
                        width={32}
                        mr={1}
                        disabled={!td.canPortCallSwapLeft || isReadOnlyScenario}
                        onPress={() =>
                          onSwapPortCall(
                            eventTimeline.id,
                            assertedDefined(
                              td.event.portCallId,
                              `expected port call event to always have portCallId`,
                            ),
                            portCallShipmentIds,
                            SwapDirection.Left,
                          )
                        }
                      >
                        ↑
                      </Button>
                      <Button
                        width={32}
                        mr={2}
                        disabled={
                          !td.canPortCallSwapRight || isReadOnlyScenario
                        }
                        onPress={() =>
                          onSwapPortCall(
                            eventTimeline.id,
                            assertedDefined(
                              td.event.portCallId,
                              `expected port call event to always have portCallId`,
                            ),
                            portCallShipmentIds,
                            SwapDirection.Right,
                          )
                        }
                      >
                        ↓
                      </Button>
                    </View>
                  )}
                </TableCell>
              )}
              <TableCell>
                <View flexDirection="row">
                  <ActivityIndicator
                    bg={
                      event.category === EventCategory.Single
                        ? getSingleEventActivityColor(event.event)
                        : getPortCallEventActivityColor(event.event)
                    }
                  />
                  <Text>
                    {event.category === EventCategory.Single
                      ? getSingleEventTypeLabel(event.event)
                      : getPortCallEventTypeLabel(event.event)}
                  </Text>
                </View>
              </TableCell>
              <TableCell>
                <Box display="flex" alignItems="center">
                  {locationName}
                  {overDraftWarning && (
                    <VesselDraftWarning draftIssueSummary={overDraftWarning} />
                  )}
                </Box>
              </TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell>
                <Box display="flex" flexDirection="row">
                  <Box display="flex" alignItems="center">
                    <Box width={200} mr={2}>
                      <StartTimePicker
                        disabled={
                          !isEditable ||
                          !timesEditable ||
                          (isPortCallEvent(event) &&
                            !!portCallTimeSourceReason(event)) ||
                          isReadOnlyScenario
                        }
                        title={
                          (isPortCallEvent(event) &&
                            portCallTimeSourceReason(event)) ||
                          timesNotEditableReason
                        }
                        isActual={
                          isPortCallEvent(event) &&
                          getPortCallTimeSourceType(
                            event.entry,
                            event.event,
                          ) === 'actual'
                        }
                        startOverride={startOverride}
                        timeViolation={timeViolation}
                        onViolationClick={handleViolationClick}
                        minDate={minStartDate}
                        onChange={(value: Date) => {
                          /**
                           * calculated ETA must always be at least 1ms lower than ETB override (if present) so if timestamp for ETB override
                           * is e.g. 16h 00m 00s 000ms then calculated ETA would be 15h 59m 59s 999ms. By setting override milliseconds to
                           * e.g. 500 on user input we get around this while maintaining the order.
                           */
                          const timestamp = new Date(value)
                            .setMilliseconds(500)
                            .valueOf();
                          // we want to add 500ms only once
                          if (Number(startOverride) === timestamp) return;

                          onEventChange(event, {
                            startOverride: timestamp,
                          });
                        }}
                        onReset={() =>
                          onEventChange(event, { startOverride: null })
                        }
                        selected={selectedDate}
                        maxDate={
                          hasSourcedEventInFuture && start
                            ? new Date(start + totalBuffer)
                            : null
                        }
                        timeZone={event.locationTimeZone ?? undefined}
                      />
                    </Box>
                    {event.category !== EventCategory.Single && (
                      <Badge
                        isRounded
                        variant={
                          sourceType[event.event] === 'actual'
                            ? 'portcalltimestatus/actual'
                            : isPast
                              ? 'past'
                              : undefined
                        }
                        marginRight={2}
                      >
                        {sourceType[event.event] === 'actual'
                          ? 'A'
                          : sourceType[event.event] === 'estimate'
                            ? 'E'
                            : 'P'}
                      </Badge>
                    )}
                  </Box>
                  {isPlanningPage && isPortCallEvent(event) && (
                    <PortCallUpdates
                      event={event}
                      earliestPossibleStart={+minStartDate}
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell />
              <TableCell />
              <TableCell>
                <DurationInput
                  overrideDuration={td.duration || duration}
                  defaultDuration={null}
                  onUpdateDuration={(updatedActualDuration) => {
                    onEventChange(event, {
                      duration:
                        event.category === EventCategory.PortCall
                          ? (updatedActualDuration ?? 0)
                          : (updatedActualDuration ?? actualDuration),
                    });
                  }}
                  disabled={
                    !durationsEditable ||
                    !!portCallDurationReason ||
                    isReadOnlyScenario
                  }
                  title={durationNotEditableReason || portCallDurationReason}
                  tabIndex={10000 + rowIndex}
                  max={hasSourcedEventInFuture ? msToH(totalBuffer) : undefined}
                />
              </TableCell>
              <TableCell></TableCell>
              <TableCell style={{ whiteSpace: 'nowrap' }}>
                {isSingleEvent(event) && (
                  <OptimisticCheckbox
                    checked={event.concurrent}
                    disabled={isReadOnlyScenario}
                    label="concurrent"
                    onChange={(newValue) =>
                      onEventChange(event, {
                        concurrent: newValue,
                      })
                    }
                  />
                )}
              </TableCell>
              {showActions && (
                <TableCell style={{ whiteSpace: 'nowrap' }}>
                  {isEditable && !isPortCallEvent(event) && (
                    <DeleteButton
                      onPress={() => {
                        onDelete(index);
                      }}
                    />
                  )}
                </TableCell>
              )}
              {showExtended && (
                <>
                  <TableCell>{td.hoursAtPort}</TableCell>
                  <TableCell>
                    {td.costs.bunkerGrade.portData?.voyageCost}
                  </TableCell>
                  <TableCell>{td.costs.tcCostPort}</TableCell>
                  <TableCell>{td.hoursAtSea}</TableCell>
                  <TableCell>
                    {Number(
                      td.costs.bunkerGrade.insideEcaData?.voyageCost ?? 0,
                    ) +
                      Number(
                        td.costs.bunkerGrade.outsideEcaData?.voyageCost ?? 0,
                      )}
                  </TableCell>
                  <TableCell>{td.costs.tcCostSea}</TableCell>
                  <TableCell>{td.mass}</TableCell>
                  <TableCell>{td.availableMassCapacity}</TableCell>
                  <TableCell>{td.volume}</TableCell>
                  <TableCell>{td.availableVolumeCapacity}</TableCell>
                </>
              )}
            </TableRow>
          </>
        );
      }
      case CoreEventType.Load:
      case CoreEventType.Discharge: {
        const { type, order, startOverride, locationId } = event;

        const { cargoUnitByCargoUnitId } = order;
        const cargoUnit = cargoUnitByCargoUnitId?.name;
        const cargoAmount =
          getCargoUnitQuantity(cargoUnit ?? '') === 'volume'
            ? systemAmountToCargoUnit(Math.abs(td.volume), cargoUnit ?? '')
            : systemAmountToCargoUnit(Math.abs(td.mass), cargoUnit ?? '');
        const isLoad = type === CoreEventType.Load;

        const isFirstLoad =
          isLoad &&
          portCallEvents.find(
            (td) => isTimeData(td) && isLoadEvent(td.event),
          ) === td;
        const isFirstDischarge =
          !isLoad &&
          portCallEvents.find(
            (td) => isTimeData(td) && isDischargeEvent(td.event),
          ) === td;

        const location = isLoad
          ? order.locationByLoadPortId
          : order.locationByDischargePortId;

        const loadingOverheadDuration = withDefined(
          isLoad
            ? order.loadingOverheadDuration
            : order.dischargingOverheadDuration,
          Number,
        );

        const m = td.mass;
        const v = td.holdVolume;

        const overCapacityVolume = Math.max(
          0,
          order.cargoRanges.volume.min - Math.abs(v),
        );

        const overCapacityMass = Math.max(
          0,
          order.cargoRanges.mass.min - Math.abs(m),
        );
        const isOverCapacity = overCapacityVolume > 0 || overCapacityMass > 0;

        /** Any orders having 0 allocations, but only relevant if allocations are in use at all in this voyage. */
        const isMissingCargoAllocations =
          voyageHasAnyTankAllocations &&
          isTCOrderEvent(event) &&
          event.cargoAllocations.every((a) => +a.amount === 0);

        const {
          value: loadingRate,
          unit: loadingUnit,
          source: loadingRateSource,
        } = isLoad
          ? (td.inputs.loadingRate ?? {})
          : (td.inputs.dischargeRate ?? {});

        const unitName = loadingUnit
          ? loadingUnitToName[loadingUnit]
          : defaultLoadingUnit.name;

        const locationName = location?.name ?? '';
        const locationTimeZone = location?.timezone ?? null;

        const isTotalDurationOverridden = !!td.event.portCallControlled;

        return (
          <TableRow
            isMuted={isMuted}
            onMouseEnter={() => setHoveredOrderId(order.id)}
            onMouseLeave={() => setHoveredOrderId(null)}
            style={{
              boxShadow: `inset 5px 0 ${
                hoveredOrderId === order.id ? '#4F88E7' : 'transparent'
              }`,
              transition: 'box-shadow .1s ease-out',
            }}
          >
            {isEditable && onSwap && (
              <TableCell pl="10px">
                <View flexDirection="row">
                  <Button
                    width={32}
                    mr={1}
                    disabled={
                      isTotalDurationOverridden ||
                      isFirstEvent ||
                      (isLoad ? false : !hasMiddle) ||
                      isFocusedShipmentShared ||
                      isReadOnlyScenario
                    }
                    onPress={() =>
                      onSwap(eventTimeline.id, index, SwapDirection.Left, td)
                    }
                  >
                    ↑
                  </Button>
                  <Button
                    width={32}
                    mr={2}
                    disabled={
                      isTotalDurationOverridden ||
                      isLastEvent ||
                      (isLoad ? !hasMiddle : false) ||
                      isFocusedShipmentShared ||
                      isReadOnlyScenario
                    }
                    onPress={() =>
                      onSwap(eventTimeline.id, index, SwapDirection.Right, td)
                    }
                  >
                    ↓
                  </Button>
                  {canHaveSingleEvents && (
                    <AddEventDropdown
                      {...{
                        onCreateSingleEvent,
                        locationId,
                        locationName,
                        locationTimeZone,
                        index,
                        start: start - 1,
                        isReadOnlyScenario,
                      }}
                    />
                  )}
                </View>
              </TableCell>
            )}
            {/** Load Discharge row */}
            <TableCell>
              <View flexDirection="row">
                <ActivityIndicator
                  bg={
                    isLoad ? 'voyageEvents.loading' : 'voyageEvents.discharge'
                  }
                />
                <Text>{isLoad ? 'Load' : 'Discharge'}</Text>
              </View>
            </TableCell>
            <TableCell>{locationName}</TableCell>
            <TableCell>{order?.assortmentByAssortmentId?.name}</TableCell>
            <TableCell>
              <Box display="flex" alignItems="center">
                {isOverCapacity ? (
                  <Tooltip
                    content={
                      <Text>
                        Capacity Over:
                        {overCapacityVolume > 0
                          ? ` ${overCapacityVolume.toFixed(2)} cbm`
                          : overCapacityMass > 0
                            ? ` ${overCapacityMass.toFixed(2)} mts`
                            : ''}
                      </Text>
                    }
                    followCursor="horizontal"
                  >
                    <Box mr={4} width="16px" height="16px">
                      <AlertIcon />
                    </Box>
                  </Tooltip>
                ) : null}
                {!isOverCapacity && isMissingCargoAllocations ? (
                  <Tooltip
                    content={
                      <Text>Cargo has no allocations to compartments</Text>
                    }
                    followCursor="horizontal"
                  >
                    <Box mr={4} width="16px" height="16px">
                      <AlertIcon />
                    </Box>
                  </Tooltip>
                ) : null}
                {Math.round(amount * 100)}% <br />
                {cargoAmount} {cargoUnit}
              </Box>
            </TableCell>
            <TableCell>
              <Tooltip
                content={
                  <Text>
                    Rate source: {formatLoadingRateSource(loadingRateSource)}
                  </Text>
                }
              >
                <div>
                  {loadingOverheadDuration
                    ? `${msToH(loadingOverheadDuration).toFixed(1)} H${
                        loadingRate ? ` + ${loadingRate} ${unitName}` : ''
                      }`
                    : `${loadingRate} ${unitName}`}
                </div>
              </Tooltip>
            </TableCell>
            <TableCell></TableCell>
            <TableCell>
              <Box
                display="inline-flex"
                flexDirection="row"
                alignItems="center"
              >
                <Box width={200} mr={2}>
                  {(!isTotalDurationOverridden ||
                    isFirstLoad ||
                    isFirstDischarge) && (
                    <StartTimePicker
                      disabled={
                        !isEditable || !timesEditable || isReadOnlyScenario
                      }
                      title={timesNotEditableReason}
                      startOverride={startOverride}
                      isActual={false}
                      timeViolation={timeViolation}
                      onViolationClick={handleViolationClick}
                      minDate={
                        eventIndex > 0
                          ? new Date(
                              eventsAndLegs[eventIndex - 1].start +
                                minimumEventDuration,
                            )
                          : undefined
                      }
                      onChange={(value: Date) => {
                        const timestamp = value.getTime();

                        onEventChange(event, {
                          startOverride: timestamp,
                        });
                      }}
                      onReset={() =>
                        onEventChange(event, { startOverride: null })
                      }
                      selected={selectedDate}
                      maxDate={
                        hasSourcedEventInFuture && start
                          ? new Date(start + totalBuffer)
                          : null
                      }
                      timeZone={event.locationTimeZone ?? undefined}
                    />
                  )}
                </Box>
              </Box>
            </TableCell>
            <TableCell />
            <TableCell>
              {work?.workshifts.length ? (
                <Tooltip
                  content={
                    <Text>
                      {work.workshifts.map((ws, index) => {
                        return ws.fromHours === ws.toHours &&
                          ws.fromMinutes === ws.toMinutes ? (
                          <Text key={index}>
                            {dayToName[ws.day]} no work
                            <br />
                          </Text>
                        ) : (
                          <Text key={index}>
                            {dayToName[ws.day]} {ws.fromHours}:
                            {ws.fromMinutes.toString().padStart(2, '0')} to{' '}
                            {ws.toHours}:
                            {Math.round(ws.toMinutes)
                              .toString()
                              .padStart(2, '0')}
                            <br />
                          </Text>
                        );
                      })}
                    </Text>
                  }
                  followCursor="horizontal"
                >
                  <Text>{oneDecimal(estimatedDurationH)} *</Text>
                </Tooltip>
              ) : (
                oneDecimal(estimatedDurationH)
              )}
            </TableCell>
            <TableCell>
              {!isTotalDurationOverridden && (
                <DurationInput
                  overrideDuration={td.event.durationOverride ?? null}
                  defaultDuration={actualDuration}
                  onUpdateDuration={(updatedActualDuration) => {
                    onEventChange(event, {
                      durationOverride: updatedActualDuration,
                    });
                  }}
                  disabled={isTotalDurationOverridden || !durationsEditable}
                  title={
                    (isTotalDurationOverridden &&
                      portCallDurationOverriddenTitle) ||
                    (durationsEditable
                      ? ''
                      : durationNotEditableReason ||
                        'Duration cannot be edited')
                  }
                  tabIndex={10000 + rowIndex}
                  max={hasSourcedEventInFuture ? msToH(totalBuffer) : undefined}
                />
              )}
              {isTotalDurationOverridden && oneDecimal(actualDurationH)}
            </TableCell>
            <TableCell
              style={{
                color: cancellingOrDeliveryViolation > 0 ? 'red' : 'orange',
              }}
              title={
                cancellingOrDeliveryViolation < -0.1
                  ? 'Early'
                  : cancellingOrDeliveryViolation > 0.1
                    ? 'Late'
                    : undefined
              }
            >
              {Math.abs(cancellingOrDeliveryViolation) > 0.1
                ? Math.round(cancellingOrDeliveryViolation)
                : ''}
            </TableCell>
            <TableCell style={{ whiteSpace: 'nowrap' }}>
              {!isTotalDurationOverridden && (
                <OptimisticCheckbox
                  checked={event.concurrent}
                  label="concurrent"
                  onChange={(newValue) =>
                    onEventChange(event, {
                      concurrent: newValue,
                    })
                  }
                  disabled={isReadOnlyScenario}
                />
              )}
            </TableCell>
            {showActions && (
              <TableCell style={{ whiteSpace: 'nowrap' }}>
                {isEditable && (
                  <DeleteButton
                    disabled={isReadOnlyScenario}
                    onPress={() => {
                      onDelete(index);
                    }}
                  />
                )}
                <Button
                  ml={3}
                  variant="secondary"
                  onPress={() =>
                    openModal({
                      type: 'VIEW_ORDER',
                      props: { orderId: order.id },
                    })
                  }
                >
                  View
                </Button>
              </TableCell>
            )}
            {showExtended && (
              <>
                <TableCell>{td.hoursAtPort}</TableCell>
                <TableCell>
                  {td.costs.bunkerGrade.portData?.voyageCost}
                </TableCell>
                <TableCell>{td.costs.tcCostPort}</TableCell>
                <TableCell>{td.hoursAtSea}</TableCell>
                <TableCell>
                  {Number(td.costs.bunkerGrade.insideEcaData?.voyageCost ?? 0) +
                    Number(
                      td.costs.bunkerGrade.outsideEcaData?.voyageCost ?? 0,
                    )}
                </TableCell>
                <TableCell>{td.costs.tcCostSea}</TableCell>
                <TableCell>{td.mass}</TableCell>
                <TableCell>{td.availableMassCapacity}</TableCell>
                <TableCell>{td.volume}</TableCell>
                <TableCell>{td.availableVolumeCapacity}</TableCell>
              </>
            )}
          </TableRow>
        );
      }
      case CoreEventType.OffHire: {
        const { locationAName, locationBName } = event;

        return (
          <TableRow isMuted={isMuted}>
            {isEditable && <TableCell />}
            <TableCell>
              <View flexDirection="row">
                <ActivityIndicator
                  bg={getSingleEventActivityColor(SingleEventType.WAITING)}
                />
                <Text>Off-Hire</Text>
              </View>
            </TableCell>
            <TableCell>
              {locationAName}
              {locationAName !== locationBName ? `, ${locationBName}` : ''}
            </TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell>
              <Box display="inline-flex" flexDirection="row">
                <Box width={200}>
                  <TimeField
                    value={start}
                    timeZone={event.locationATimeZone ?? undefined}
                  />
                </Box>
              </Box>
            </TableCell>
            <TableCell>{actualDurationH}</TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            {showActions && (
              <TableCell style={{ whiteSpace: 'nowrap' }}>
                {isEditable && (
                  <DeleteButton
                    onPress={() => {
                      onDelete(index);
                    }}
                  />
                )}
              </TableCell>
            )}
          </TableRow>
        );
      }
      case CoreEventType.Static: {
        const {
          entry,
          locationAName,
          locationATimezone,
          locationBName,
          locationBTimezone,
        } = event;
        const { delivery, redelivery } = entry;

        return (
          <>
            {redelivery ? (
              <TableRow isMuted={isMuted}>
                {isEditable && <TableCell />}
                <TableCell>
                  <View flexDirection="row">
                    <ActivityIndicator
                      bg={getSingleEventActivityColor(SingleEventType.WAITING)}
                    />
                    <Text>TC In Redelivery</Text>
                  </View>
                </TableCell>
                <TableCell>{locationAName}</TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell>
                  <Box display="inline-flex" flexDirection="row">
                    <Box width={200}>
                      <TimeField value={start} timeZone={locationATimezone} />
                    </Box>
                  </Box>
                </TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                {showActions && <TableCell />}
              </TableRow>
            ) : null}
            {delivery ? (
              <TableRow isMuted={isMuted}>
                {isEditable && <TableCell />}
                <TableCell>
                  <View flexDirection="row">
                    <ActivityIndicator
                      bg={getSingleEventActivityColor(SingleEventType.WAITING)}
                    />
                    <Text>TC In Delivery</Text>
                  </View>
                </TableCell>
                <TableCell>{locationBName}</TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell>
                  <Box display="inline-flex" flexDirection="row">
                    <Box width={200}>
                      <TimeField value={end} timeZone={locationBTimezone} />
                    </Box>
                  </Box>
                </TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                {showActions && <TableCell />}
              </TableRow>
            ) : null}
          </>
        );
      }
      default: {
        exhaustiveCheck(event);
      }
    }
  },
);

type ShippingPlanRowData = {
  eventIndex: number;
} & (
  | {
      data: PortCallTotalDurationData;
    }
  | {
      event: EventOrLeg;
    }
);

const PortCallUpdatesFetcher = ({
  eventsAndLegs,
}: {
  eventsAndLegs: EventsAndLegs;
}) => {
  const { getPortCallUpdates } = usePortCallUpdates();

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const portCallEntries = useMemo(() => {
    const entries = eventsAndLegs
      .filter(
        (td): td is TimeData<PortCallEvent> =>
          isTimeData(td) && isPortCallEventTimeData(td),
      )
      .map((td) => td.event.entry);

    return mapKeyBy(entries, (e) => e.clientId);
  }, [eventsAndLegs]);

  /** Keep track of what we already fetched to make sure that we never fall into an endless refetch loop (rel. SEABER-4180 Fix port call updates refresh loop) */
  const lastFetchedPCClientIds = useRef<ClientId[] | null>(null);
  const [updateStatus, setUpdateStatus] = useState('');

  useEffect(() => {
    const getUpdates = async () => {
      if (loading) return;
      const ids = Array.from(portCallEntries.keys()).sort();
      if (isEqual(ids, lastFetchedPCClientIds.current)) return;
      lastFetchedPCClientIds.current = ids;
      setLoading(true);
      setError(null);
      try {
        const result = await getPortCallUpdates(portCallEntries);
        setUpdateStatus(result || '');
      } catch (err) {
        setError(err.message);
      }
      setLoading(false);
    };

    getUpdates();
  }, [loading, getPortCallUpdates, portCallEntries]);

  if (loading) return <div>Loading updates...</div>;
  if (error) return <div>Error fetching updates: {error}</div>;
  return (
    <div>
      Updates fetched successfully {updateStatus ? `: ${updateStatus}` : null}
    </div>
  );
};

const PortCallUpdates = ({
  event,
  earliestPossibleStart,
}: {
  event: PortCallEvent;
  earliestPossibleStart: number;
}) => {
  const { applyPortCallUpdate, updates: allUpdates } = usePortCallUpdates(
    event.portCallId !== null ? [event.portCallId] : [],
  );

  const { portCallId, event: eventType } = event;

  if (portCallId === null) return null;

  const updates =
    [...allUpdates].filter((update) => update.type === eventType) ?? [];

  const newCount = updates.filter((u) => u.isNew).length;

  // Consider empty string as null source
  const isSourced = !!getPortCallSourceNameForPortCall(
    event.entry,
    event.event,
  );

  return updates.length > 0 || isSourced ? (
    <DropdownButton
      variant="secondary"
      label={
        <Box
          display="inline-flex"
          flexDirection="row"
          flexWrap="nowrap"
          alignItems="center"
        >
          {newCount > 0 ? (
            <Badge variant="status/unassigned" isRounded mr={2}>
              {newCount}
            </Badge>
          ) : null}
          Updates
        </Box>
      }
      ml={2}
    >
      <Menu hasShadow>
        {updates.map((update) => (
          <PortCallUpdateMenuItem
            key={`${update.portCallId}${update.source}${update.type}${update.value}`}
            update={update}
            onClick={() => applyPortCallUpdate(update)}
            timezone={event.locationTimeZone ?? undefined}
            isInvalid={update.value < earliestPossibleStart}
          />
        ))}
        {isSourced && (
          <Box
            p={3}
            display="flex"
            flexDirection="row"
            alignItems="center"
            onClick={() => {
              applyPortCallUpdate({
                portCallId,
                source: null,
                sourceType: 'estimate',
                updatedAt: new Date().getTime(),
                type: eventType,
                // We slightly modify value so changes will be applied
                // Without modification port call will not be changed and
                //  there will be an exception in addTimeSourceToResult
                value: (event.startOverride || event.start) + 1,
                isConfirmed: null,
              });
            }}
          >
            <span>Set manually</span>
          </Box>
        )}
      </Menu>
    </DropdownButton>
  ) : null;
};

const getBerthFontColor = (v: number | null): string => {
  if (v === null) return 'black';
  return v === 0 ? 'red' : v < 4 ? 'orange' : 'black';
};

export type BerthPredictionToolTipContent = {
  prediction?: CachedBerthPrediction['prediction'];
  input?: CachedBerthPrediction['input'] | null;
  mapping?: CachedBerthPrediction['mapping'] | null;
  durations?: string;
};

const BerthPredictionToolTip = React.memo(
  ({
    prediction,
    input,
    mapping,
    durations,
  }: BerthPredictionToolTipContent): JSX.Element => {
    const renderItem = (
      key: string,
      value: number | string | boolean | undefined | null,
      color?: string,
    ) => {
      if (key.includes('typename')) return null;
      return (
        <Box key={key} display="flex">
          <Text fontSize={1} mx={3}>
            {key.replace(/_/g, ' ')}:
          </Text>
          <Text fontSize={1} color={color}>
            {value}
          </Text>
        </Box>
      );
    };

    return (
      <>
        {prediction ? (
          <>
            <Box display="flex" flexDirection="column" mb={4}>
              <Text fontSize={1}>prediction: &#123;</Text>
              {Object.keys(prediction).map(
                (key: keyof CachedBerthPrediction['prediction']) =>
                  renderItem(key, prediction[key]),
              )}
              <Text fontSize={1}>&#125;</Text>
            </Box>

            {input && (
              <Box display="flex" flexDirection="column" mb={4}>
                <Text fontSize={1}>input: &#123;</Text>
                {Object.keys(input).map(
                  (key: keyof CachedBerthPrediction['input']) =>
                    renderItem(key, input[key]),
                )}
                <Text fontSize={1}>&#125;</Text>
              </Box>
            )}

            {mapping && (
              <Box display="flex" flexDirection="column">
                <Text fontSize={1}>mapping: &#123;</Text>
                {Object.keys(mapping).map(
                  (key: keyof CachedBerthPrediction['mapping']) =>
                    renderItem(key, mapping[key]),
                )}
                <Text fontSize={1}>&#125;</Text>
              </Box>
            )}
          </>
        ) : (
          <Text>{durations}</Text>
        )}
      </>
    );
  },
  isEqual,
);

type PortCallTotalDurationData = {
  key: string;
  eventType: CoreEventType.Load | CoreEventType.Discharge;
  locationName: string;
  numRelatedEvents: number;
  disabled: boolean;
  disabledReason: string | null;
  duration: number | null;
  onChangeDuration: (newDuration: number | null) => void;
  clientId: ClientId | null;
  durations: number[];
};

type PortCallTotalDurationRowProps = {
  tabIndex: number;
  isMuted: boolean;
  isBerthPredictionAvailable: boolean;
  isEditable: boolean;
  berthPredictions: DefaultMap<
    ClientId,
    Map<'L' | 'D', CachedBerthPrediction | null | undefined>
  >;
  showExtended: boolean;
} & PortCallTotalDurationData;

/**
 *  It generates Total loading/discharging row
 */
const PortCallTotalDurationRow = (
  props: PortCallTotalDurationRowProps,
): JSX.Element => {
  const {
    berthPredictions,
    clientId,
    disabled,
    disabledReason,
    duration,
    eventType,
    isMuted,
    locationName,
    numRelatedEvents,
    onChangeDuration,
    tabIndex,
    isBerthPredictionAvailable,
    durations,
    isEditable,
    showExtended,
  } = props;
  const isLoad = eventType === CoreEventType.Load;
  const result = getBerthPrediction(clientId, berthPredictions, isLoad);
  const input = result?.input;
  const mapping = result?.mapping;
  const prediction = result?.prediction;
  const pred = prediction?.pred;
  const durationSum = oneDecimal(sum(durations));
  const shownValue =
    pred != null
      ? Math.ceil(pred)
      : !isBerthPredictionAvailable
        ? durationSum
        : null;

  const berthFontColor = getBerthFontColor(
    mapping?.vessel_name_cargo_n ?? null,
  );

  return (
    <TableRow isMuted={isMuted}>
      {isEditable && <TableCell />}
      <TableCell>
        <View flexDirection="row">
          <ActivityIndicator
            bg={isLoad ? 'voyageEvents.loading' : 'voyageEvents.discharge'}
          />
          <Text>Total {isLoad ? 'loading' : 'discharging'}</Text>
        </View>
      </TableCell>
      <TableCell>{locationName}</TableCell>
      <TableCell>{numRelatedEvents} orders</TableCell>
      <TableCell />
      <TableCell />
      <TableCell />
      <TableCell />
      <TableCell />
      <TableCell
        onClick={() =>
          shownValue != null && onChangeDuration(hToMs(shownValue))
        }
        cursor={shownValue != null ? 'pointer' : 'default'}
      >
        <Tooltip
          content={
            <BerthPredictionToolTip
              {...{
                prediction,
                input,
                mapping,
                durations:
                  duration !== null
                    ? `Total: ${msToH(duration)}`
                    : durations.map(oneDecimal).join(' + '),
              }}
            />
          }
        >
          <Text color={berthFontColor}>
            {pred
              ? `Api: ${shownValue}`
              : isBerthPredictionAvailable
                ? 'fetching'
                : durationSum}
          </Text>
        </Tooltip>
      </TableCell>
      <TableCell>
        <DurationInput
          disabled={disabled}
          title={disabledReason ?? ''}
          tabIndex={tabIndex}
          overrideDuration={disabled ? hToMs(durationSum) : duration}
          defaultDuration={null}
          onUpdateDuration={onChangeDuration}
        />
      </TableCell>
      <TableCell />
      <TableCell />
      <TableCell />
      {showExtended && <TableCell colSpan={10} />}
    </TableRow>
  );
};

const PortRotationTable = ({
  currentUser,
  isEditable,
  isFocusedShipmentShared,
  eventsAndLegs,
  timeData,
  totals,
  inputs,
  onCreateSingleEvent,
  onOptimizeVoyageSpeed,
  hasMoreEvents,
  onShowMoreEvents,
  onEventChange,
  berthPredictions,
  isBerthPredictionAvailable,
  isChannelUsageToggleAvailable,
  vessel,
  scheduleContext,
  shipment,
  isReadOnlyScenario,
  ...restProps
}: PortRotationTableProps): JSX.Element => {
  const router = useRouter();
  const isPlanningPage = router.pathname === '/planning';
  const theme = useTheme();
  const first = eventsAndLegs[0];
  const last = eventsAndLegs[eventsAndLegs.length - 1];

  const totalRotationDurationMs = first && last ? last.end - first.start : 0;

  const isRootTimeline = !restProps.eventTimeline.parent;

  const showActions = useMemo(
    () =>
      currentUser.organization?.charterer ||
      typeof currentUser.organization?.parentOrganizationId !== 'number',
    [currentUser.organization],
  );

  const isAutoAcceptEnabled =
    currentUser?.rootOrganization?.configAutoAcceptUpdates ?? false;

  const [showExtendedInfo, setShowExtendedInfo] = useState(false);
  const [editingRowIndex, setEditingRowIndex] = useState(-1);

  const numColumns =
    (showExtendedInfo ? 21 : 11) +
    (isEditable && showActions ? 2 : isEditable || showActions ? 1 : 0);

  const rowData: ShippingPlanRowData[] = useMemo(() => {
    let eventIndex = 0;
    return groupBySequence(eventsAndLegs, (td) =>
      'event' in td ? getEventLocationId(td.event) : undefined,
    ).flatMap((locationEvents) => {
      /** Pick durations for each given event. In case of concurrent events, returns their shared max value as one number.
       * So, the result array may be shorter than the input. */
      const collectDurations = (xs: TimeData[]): number[] => {
        return xs.reduce((acc: number[], current) => {
          let result = msToH(current.duration);
          const lastDuration = get(acc, acc.length - 1);
          if (
            isCargoEventTimeData(current) &&
            current.event.concurrent &&
            lastDuration !== undefined
          ) {
            result = Math.max(result, lastDuration);
            acc[acc.length - 1] = result;
          } else {
            acc.push(result);
          }
          return acc;
        }, []);
      };

      // Loading
      const loads = locationEvents
        .filter(isTimeData)
        .filter((td) => isLoadEvent(td.event));
      const numLoads = loads.length;
      const loadDurations = collectDurations(loads);
      // Discharging
      const discharges = locationEvents
        .filter(isTimeData)
        .filter((td) => isDischargeEvent(td.event));
      const numDischarges = discharges.length;
      const dischargeDurations = collectDurations(discharges);

      const arrivalTd = locationEvents.find(
        (td) => isTimeData(td) && isPortCallArrivalEvent(td.event),
      );
      const arrival =
        arrivalTd && assertedCast(arrivalTd.event, isPortCallArrivalEvent);

      const result: ShippingPlanRowData[] = [];

      locationEvents.forEach((td) => {
        if (
          isTimeData(td) &&
          isPortCallEventTimeData(td) &&
          td.event.event === PortCallEventType.Departure &&
          arrival
        ) {
          const disabled = !isEditable || !!td.event.sofDuration;
          const disabledReason = disabled
            ? td.event.sofDuration
              ? 'Duration comes from SOF'
              : null
            : null;

          if (numDischarges > 0) {
            const eventType = CoreEventType.Discharge;
            const row: PortCallTotalDurationData = {
              eventType,
              disabled,
              disabledReason,
              duration: arrival?.totalDischargeDurationOverride ?? null,
              key: `portcall-${td.event.portCallId}-duration-${eventType}`,
              locationName: td.event.locationName,
              numRelatedEvents: numDischarges,
              onChangeDuration: (newDuration) => {
                onEventChange(arrival, {
                  totalDischargeDurationOverride: newDuration,
                });
              },
              clientId: td.event.portCallId,
              durations: dischargeDurations,
            };

            const lastEvent = result[result.length - 1];
            if (
              lastEvent &&
              'event' in lastEvent &&
              'eventType' in lastEvent.event &&
              lastEvent.event.eventType === ImplicitEventType.IDLE
            ) {
              result.pop();
              result.push({ eventIndex: eventIndex - 1, data: row });
              result.push(lastEvent);
            } else {
              result.push({ eventIndex, data: row });
            }
          }

          if (numLoads > 0) {
            const eventType = CoreEventType.Load;
            const row: PortCallTotalDurationData = {
              eventType,
              disabled,
              disabledReason,
              duration: arrival?.totalLoadDurationOverride ?? null,
              key: `portcall-${td.event.portCallId}-duration-${eventType}`,
              locationName: td.event.locationName,
              numRelatedEvents: numLoads,
              onChangeDuration: (newDuration) => {
                onEventChange(arrival, {
                  totalLoadDurationOverride: newDuration,
                });
              },
              clientId: td.event.portCallId,
              durations: loadDurations,
            };

            const lastEvent = result[result.length - 1];
            if (
              lastEvent &&
              'event' in lastEvent &&
              'eventType' in lastEvent.event &&
              lastEvent.event.eventType === ImplicitEventType.IDLE
            ) {
              result.pop();
              result.push({ eventIndex: eventIndex - 1, data: row });
              result.push(lastEvent);
            } else {
              result.push({ eventIndex, data: row });
            }
          }
        }

        result.push({ eventIndex, event: td });
        eventIndex++;
      });

      return result;
    });
  }, [eventsAndLegs, isEditable, onEventChange]);

  const violationContextMenuEvent = useRef<EventOrLeg | null>(null);

  const {
    onContextMenu: onViolationContextMenu,
    hideMenu: hideViolationMenu,
    styles: violationMenuStyles,
    isVisible: isViolationMenuVisible,
    setMenuRef: setViolationMenuRef,
  } = useContextMenu();

  const openViolationContextMenu = useCallback(
    (event: EventOrLeg, e: React.MouseEvent) => {
      violationContextMenuEvent.current = event;
      onViolationContextMenu(e);
    },
    [onViolationContextMenu],
  );

  /** Updates the event specified by violationContextMenuEvent or the event before it
   * so that previous duration will match start override - previous start. Which value
   * to update to meet the other is given as the parameter.
   */
  const resolveViolation = useCallback(
    (updateWhich: 'duration' | 'start') => {
      hideViolationMenu();

      const target = assertedDefined(
        violationContextMenuEvent.current,
        `expected a targeted event to resolve time violation for`,
      );
      if (!isTimeData(target)) {
        throw new Error(
          `can only resolve time violations on timedata events, not legs or waits`,
        );
      }
      assertDefined(
        target.event.startOverride,
        `start override not set, there should be no timing conflict`,
      );
      const targetIndex = eventsAndLegs.findIndex((td) => td === target);
      const prevEvent = get(eventsAndLegs, targetIndex - 1);
      assertDefined(
        prevEvent,
        `targeted event to resolve not found in events array`,
      );
      if (updateWhich === 'duration') {
        const newDuration = target.event.startOverride - prevEvent.start;
        if (isLeg(prevEvent)) {
          onEventChange(target.event, {
            seaDurationOverride: newDuration,
          });
        } else if (isTimeData(prevEvent)) {
          onEventChange(
            prevEvent.event,
            isSingleEvent(prevEvent.event) || isPortCallEvent(prevEvent.event)
              ? {
                  duration: newDuration,
                }
              : {
                  durationOverride: newDuration,
                },
          );
        } else {
          throw new Error(
            `previous event is an Idle, there should be no timing conflict`,
          );
        }
      } else {
        onEventChange(target.event, {
          startOverride: target.start,
        });
      }
    },
    [eventsAndLegs, hideViolationMenu, onEventChange],
  );

  return (
    <View>
      <Heading fontSize={4} fontWeight="bold" my={4}>
        Port rotation
      </Heading>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box as="label" display="inline-box" alignItems="center" mb={2}>
          <input
            type="checkbox"
            name="extended_info"
            checked={showExtendedInfo}
            onChange={() => setShowExtendedInfo(!showExtendedInfo)}
          />
          <Text ml={1}>Extended info</Text>
        </Box>
        {isPlanningPage && !isAutoAcceptEnabled && (
          <PortCallUpdatesFetcher eventsAndLegs={eventsAndLegs} />
        )}
      </Box>
      <Table data-testid="table-port-rotation">
        <thead style={{ border: `1px solid ${theme.colors.neutral.gray_225}` }}>
          <TableHeaders>
            {isEditable && <TableHeader pl="10px">Order</TableHeader>}
            <TableHeader>Activity</TableHeader>
            <TableHeader>Port</TableHeader>
            <TableHeader>Commodity</TableHeader>
            <TableHeader>Amount</TableHeader>
            <TableHeader>Speed</TableHeader>
            <TableHeader>Distance</TableHeader>
            <TableHeader>Start</TableHeader>
            <TableHeader>Weather factor</TableHeader>
            <TableHeader maxWidth="6em">Estimated duration (h)</TableHeader>
            <TableHeader maxWidth="6em">Duration (h)</TableHeader>
            <TableHeader>Late (h)</TableHeader>
            <TableHeader>Options</TableHeader>
            {showActions && <TableHeader>Action</TableHeader>}
            {showExtendedInfo && (
              <>
                <TableHeader>Port hours</TableHeader>
                <TableHeader>Port bunker</TableHeader>
                <TableHeader>Port cost</TableHeader>
                <TableHeader>Sea hours</TableHeader>
                <TableHeader>Sea bunker</TableHeader>
                <TableHeader>Sea cost</TableHeader>
                <TableHeader>Transferred mass</TableHeader>
                <TableHeader>Mass capacity</TableHeader>
                <TableHeader>Transferred volume</TableHeader>
                <TableHeader>Volume capacity</TableHeader>
              </>
            )}
          </TableHeaders>
        </thead>
        <tbody style={{ border: `1px solid ${theme.colors.neutral.gray_225}` }}>
          <TableRow isMuted={false}>
            {isRootTimeline ? (
              <>
                <TableCell colSpan={numColumns / 2} textAlign="center">
                  <Button
                    disabled={!hasMoreEvents[0]}
                    onPress={() => onShowMoreEvents('beforeAll')}
                    variant="secondary"
                    margin={2}
                  >
                    Show previous all
                  </Button>
                </TableCell>
                <TableCell colSpan={numColumns / 2} textAlign="center">
                  <Button
                    disabled={!hasMoreEvents[0]}
                    onPress={() => onShowMoreEvents('before')}
                    variant="secondary"
                    margin={2}
                  >
                    Show previous week
                  </Button>
                </TableCell>
              </>
            ) : (
              <TableCell colSpan={numColumns} textAlign="center">
                <Button
                  disabled={restProps.prevVoyageIndex <= 0}
                  onPress={() => onShowMoreEvents('prev')}
                  variant="secondary"
                  margin={2}
                >
                  Show previous
                </Button>
              </TableCell>
            )}
          </TableRow>
          {rowData.map((row, i) =>
            'data' in row ? (
              <PortCallTotalDurationRow
                isMuted={i % 2 === 0}
                tabIndex={10000 + i}
                berthPredictions={berthPredictions}
                isBerthPredictionAvailable={isBerthPredictionAvailable}
                isEditable={isEditable}
                showExtended={showExtendedInfo}
                {...row.data}
                key={row.data.key}
              />
            ) : i === editingRowIndex &&
              scheduleContext &&
              'from' in row.event ? (
              <EditingPortRotationRow
                rowIndex={i}
                td={row.event}
                isMuted={i % 2 === 0}
                setEditingRowIndex={setEditingRowIndex}
                onCreateSingleEvent={onCreateSingleEvent}
                eventTimeline={restProps.eventTimeline}
                timeData={timeData}
                eventsAndLegs={eventsAndLegs}
                isFocusedShipmentShared={isFocusedShipmentShared}
              />
            ) : (
              <PortRotationRow
                isMuted={i % 2 === 0}
                td={row.event}
                rowData={rowData}
                rowIndex={i}
                eventIndex={row.eventIndex}
                key={`${row.event.key}-${i}`}
                {...restProps}
                onCreateSingleEvent={onCreateSingleEvent}
                timeData={timeData}
                eventsAndLegs={eventsAndLegs}
                isEditable={isEditable}
                showActions={showActions}
                showExtended={showExtendedInfo}
                onEventChange={onEventChange}
                onOptimizeVoyageSpeed={onOptimizeVoyageSpeed}
                isPlanningPage={isPlanningPage}
                isChannelUsageToggleAvailable={isChannelUsageToggleAvailable}
                onViolationMenu={openViolationContextMenu}
                setEditingRowIndex={setEditingRowIndex}
                isFocusedShipmentShared={isFocusedShipmentShared}
                isReadOnlyScenario={isReadOnlyScenario}
              />
            ),
          )}
          <TableRow isMuted={eventsAndLegs.length % 2 === 0}>
            {isRootTimeline ? (
              <>
                <TableCell colSpan={numColumns / 2} textAlign="center">
                  <Button
                    disabled={!hasMoreEvents[1]}
                    onPress={() => onShowMoreEvents('afterAll')}
                    variant="secondary"
                    margin={2}
                  >
                    Show next all
                  </Button>
                </TableCell>
                <TableCell colSpan={numColumns / 2} textAlign="center">
                  <Button
                    disabled={!hasMoreEvents[1]}
                    onPress={() => onShowMoreEvents('after')}
                    variant="secondary"
                    margin={2}
                  >
                    Show next week
                  </Button>
                </TableCell>
              </>
            ) : (
              <TableCell colSpan={numColumns} textAlign="center">
                <Button
                  disabled={
                    restProps.eventTimeline?.parent &&
                    (restProps.eventTimeline.type !== 'tc'
                      ? restProps.nextVoyageIndex >=
                        restProps.voyageGroups.length - 1
                      : restProps.nextVoyageIndex >
                        restProps.voyageGroups.length - 1)
                  }
                  onPress={() => onShowMoreEvents('next')}
                  variant="secondary"
                  margin={2}
                >
                  Show next
                </Button>
              </TableCell>
            )}
          </TableRow>
        </tbody>
        <tfoot
          style={{ borderTop: `1px solid ${theme.colors.neutral.gray_225}` }}
        >
          <TableRow>
            {isEditable && <TableCell />}
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell style={{ whiteSpace: 'nowrap' }}>
              {Math.round(totals.mass)} mts
            </TableCell>
            <TableCell />
            <TableCell>{Math.round(totals.distance)} nm</TableCell>
            <TableCell />
            <TableCell />
            <TableCell>
              {Math.round(msToH(totalRotationDurationMs))} h
            </TableCell>
            <TableCell />
            <TableCell style={{ whiteSpace: 'nowrap' }}>
              {Math.round(totals.delays)} h
            </TableCell>
            <TableCell />
            {showActions && <TableCell />}
          </TableRow>
        </tfoot>
      </Table>
      {showExtendedInfo ? (
        <Box display="flex">
          <Box>
            <h4 style={{ marginBottom: 0 }}>Vessel / cost specs:</h4>
            <p>
              {inputs?.tcRate ? (
                <dt>
                  TC rate: <b>{inputs.tcRate}</b>
                </dt>
              ) : null}
              {inputs?.volumeCapacity ? (
                <dt>
                  Volume capacity: <b>{inputs.volumeCapacity}</b>
                </dt>
              ) : null}
              {inputs?.weightCapacity ? (
                <dt>
                  Weight capacity: <b>{inputs.weightCapacity}</b>
                </dt>
              ) : null}
            </p>
          </Box>
          <Box ml={20}>
            <h4 style={{ marginBottom: 0 }}>Shipment info:</h4>
            <p>
              {shipment?.id ? (
                <dt>
                  Shipment Id: <b>{shipment.id}</b>
                </dt>
              ) : null}
              {shipment?.externalShipmentId ? (
                <dt>
                  External Shipment Id: <b>{shipment.externalShipmentId}</b>
                </dt>
              ) : null}
              {shipment?.externalShipmentStatus ? (
                <dt>
                  External Shipment Status:{' '}
                  <b>{shipment.externalShipmentStatus}</b>
                </dt>
              ) : null}
            </p>
          </Box>
        </Box>
      ) : null}
      <div ref={setViolationMenuRef} style={violationMenuStyles}>
        {isViolationMenuVisible && (
          <Menu hasShadow>
            <MenuItem
              label="Shorten previous duration to meet start override"
              onPress={() => resolveViolation('duration')}
            />
            <MenuItem
              label="Update start override to match current start time"
              onPress={() => resolveViolation('start')}
            />
          </Menu>
        )}
      </div>
    </View>
  );
};

export type EventsWithPortCalls = { portCallId?: string } & Pick<
  VoyageGroup,
  'eventsAndLegs'
>;

export interface ShippingPlanProps {
  isEditable: boolean;
  vessel?: ShippingPlan_ShipFragment;
  focusedOrders: ScenarioOrder[];
  eventTimeline: EventTimeline;
  shipInput: TimeDataInputs | undefined;
  onUnassign: OrderTableProps['onUnassign'];
  timelineFocus?: TimelineFocus;
  onEventChange: (event: Event, change: Partial<Event>) => void;
  onSwap?: ScenarioTimelineProps['onSwapScheduleEntry'];
  onSwapPortCall: ScenarioTimelineProps['onSwapPortCall'];
  onDelete: (index: number) => void;
  onCreateSingleEvent: (
    type: SingleEventType,
    locationId: number,
    locationName: string,
    locationTimeZone: string | null,
    index: number,
    start: number,
    duration?: number,
  ) => void;
  onOptimizeVoyageSpeed: PortRotationTableProps['onOptimizeVoyageSpeed'];
  currentUser: User;
  isVesselChangeEnabled: boolean;
  onVesselChange: (vesselId: number) => void;
  coaId: number | null;
  voyageCharterId: number | null;
  scheduleContext?: ScheduleDataContext;
  onVoyageCharterUpdateApprove?: ShipmentVoyageCharterUpdatesProps['onApprove'];
  scenario: ScenarioSchedule;
}

/** Interleaves all groups of events without rearranging events within each group. */
const getSortedEventsAndLegs = (
  vgs: EventsWithPortCalls[],
): EventOrLeg<Events>[] =>
  vgs.reduce(
    (acc, vg) =>
      interleave(acc, vg.eventsAndLegs, (a, b) =>
        a.start <= b.start
          ? InterleaveItemResult.LEFT
          : InterleaveItemResult.RIGHT,
      ),
    [] as EventOrLeg<Events>[],
  );

export function getVoyageCalculationInputs(
  vessel: ShippingPlan_ShipFragment | undefined,
  shownEventsAndLegs: (Leg | WaitEvent | TimeData)[],
  bunkerPriceByGradeName: (gradeName: string) => number | null,
) {
  const inputs = new Map<number, CalculateVoyageInputs>();
  const imo = vessel?.imo;
  if (imo != null) {
    const draft = +(vessel?.draft ?? 7);
    const price = bunkerPriceByGradeName('MGO');
    const fuels: FuelInput | null = price
      ? {
          availableFuels: [
            {
              type: 'MGO',
              price,
            },
          ],
          insideEca: 'MGO',
          outsideEca: 'MGO',
        }
      : null;
    shownEventsAndLegs.forEach((td) => {
      if (isLeg(td)) {
        const { event, start, end, avoidsChannels } = td;
        const { locationAId, locationBId } = event;
        const input: CalculateVoyageInputs = {
          imoNumber: +imo,
          from: locationAId,
          to: locationBId,
          startTime: new Date(start).toISOString(),
          operationMethod: {
            arrivalTime: new Date(end).toISOString(),
          },
          draft,
          avoidsChannels,
        };
        if (fuels) {
          input.fuels = fuels;
        }
        inputs.set(td.index, input);
      }
    });
  }
  return inputs;
}

function useCalculateVoyages(
  isVoyageCalculationAvailable: boolean,
  vessel: ShippingPlan_ShipFragment | undefined,
  shownEventsAndLegs: (Leg | WaitEvent | TimeData)[],
) {
  const { bunkerPriceByGradeName } = useBunkerPrices();
  const inputs = useMemo(
    () =>
      getVoyageCalculationInputs(
        vessel,
        shownEventsAndLegs,
        bunkerPriceByGradeName,
      ),
    [vessel, shownEventsAndLegs, bunkerPriceByGradeName],
  );
  const voyageInputs = useMemo(() => Array.from(inputs.values()), [inputs]);

  const { data: voyageCalculations } = useCalculateVoyagesQuery({
    variables: {
      inputs: voyageInputs,
    },
    skip: !isVoyageCalculationAvailable || !voyageInputs.length,
  });

  return useMemo(() => {
    const voyageResults = new Map<
      number,
      Pick<VoyageCalculation, 'fuelConsumptionBreakdown'>
    >();

    const voyageIndices = Array.from(inputs.keys());

    voyageCalculations?.calculateVoyages?.forEach((voyage, index) => {
      if (!voyage) return;
      voyageResults.set(voyageIndices[index], voyage);
    });

    return voyageResults;
  }, [voyageCalculations, inputs]);
}

function ShipmentOptions({
  scenario,
  shownOrders,
  shipmentId,
  remarks,
  voyageNamingRuleByShipId,
  vessel,
  externalShipmentId,
}: {
  scenario:
    | UserSchedule
    | SuggestionSchedule
    | MasterSchedule
    | BackupSchedule
    | InitialSchedule;
  shownOrders: ScenarioOrder[];
  shipmentId: number;
  remarks: string;
  voyageNamingRuleByShipId: Map<
    number,
    {
      rel_id: number;
      rule: VoyageNamingRule_AdminFragment;
    }
  >;
  vessel?: ShippingPlan_ShipFragment;
  externalShipmentId: string | null;
}) {
  const client = useApolloClient();

  const [newRemarks, setRemarks] = useState(remarks);
  const savingStatus = useRef<'success' | 'error' | null>(null);
  const [updateShipmentMutation, { loading, error }] =
    useUpdateShipmentMutation();

  const updateRemarksHandler = useEvent(async () => {
    try {
      await updateShipmentMutation({
        variables: {
          shipmentId,
          patch: {
            remarks: newRemarks,
          },
        },
      });
      savingStatus.current = 'success';
      setTimeout(() => {
        savingStatus.current = null;
      }, 4000);
    } catch (_err) {
      savingStatus.current = 'error';
    }
  });

  const regenShipmentNameHandler = useEvent(async () => {
    try {
      const allScenarioOrderAssignments = getOrderAssignments(scenario);
      const namingRule = voyageNamingRuleByShipId.get(vessel?.id ?? -1);

      const shipmentNamePrefix = namingRule
        ? getShipmentPrefixWithRule(namingRule.rule, vessel)
        : getShipmentPrefix(shownOrders, allScenarioOrderAssignments);

      const shipmentNames = await fetchShipmentNames(client, [
        shipmentNamePrefix,
      ]);

      await updateShipmentMutation({
        variables: {
          shipmentId,
          patch: {
            name: shipmentNames[0],
          },
        },
      });
      savingStatus.current = 'success';
      setTimeout(() => {
        savingStatus.current = null;
      }, 4000);
    } catch (_err) {
      savingStatus.current = 'error';
    }
  });

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '8px',
          gap: '24px',
        }}
      >
        <Button
          disabled={loading}
          variant="secondary"
          alignSelf="flex-end"
          onPress={regenShipmentNameHandler}
          display="flex"
          alignItems="center"
        >
          Regenerate Voyage Number
        </Button>
        {shipmentId ? (
          <dt>
            Seaber ID: <b>{shipmentId}</b>
          </dt>
        ) : null}

        {externalShipmentId ? (
          <dt>
            External Voyage ID: <b>{externalShipmentId}</b>
          </dt>
        ) : null}
      </Box>
      <Box
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
        }}
      >
        <Input
          type="text"
          disabled={loading}
          value={newRemarks}
          placeholder="Remarks ..."
          onChange={(e) => {
            if (savingStatus.current !== null) {
              savingStatus.current = null;
            }
            setRemarks(e.target.value);
          }}
        />
        <Button
          disabled={loading}
          variant="secondary"
          width="fit-content"
          alignSelf="flex-end"
          onPress={updateRemarksHandler}
          mb={8}
          ml={8}
        >
          Save remark
        </Button>
        {savingStatus.current === 'success' ? (
          <Text mb={8} fontWeight="bold" color="status.success">
            ✓ Saved successfully
          </Text>
        ) : savingStatus.current === 'error' ? (
          <Text mb={8} fontWeight="bold" color="status.error">
            ✗ Error saving {error?.message}
          </Text>
        ) : null}
      </Box>
    </Box>
  );
}

const hoveredOrderIdAtom = atom<number | null>(null);

function isInFilterRange(
  time: number,
  fromDate: Date | null,
  toDate: Date | null,
) {
  const from = fromDate?.getTime() ?? null;
  const to = toDate?.getTime() ?? null;
  return (from ? time >= from : true) && (to ? time <= to : true);
}

const ShippingPlan = ({
  isEditable,
  onEventChange,
  onSwap,
  onSwapPortCall,
  onDelete,
  onCreateSingleEvent,
  onOptimizeVoyageSpeed,
  eventTimeline,
  shipInput,
  onUnassign,
  currentUser,
  vessel,
  isVesselChangeEnabled,
  onVesselChange,
  coaId,
  voyageCharterId,
  scheduleContext,
  onVoyageCharterUpdateApprove,
  scenario,
  focusedOrders,
}: ShippingPlanProps): JSX.Element => {
  const orders = useMemo(
    () => eventTimeline.voyageGroups.flatMap((vg) => vg.orders),
    [eventTimeline.voyageGroups],
  );
  const focusedOrderIds = useMemo(
    () => new Set(focusedOrders.map((o) => o.id)),
    [focusedOrders],
  );
  const isReadOnlyScenario = scenario.isReadOnly;
  const isTcVoyage = eventTimeline?.type === 'tc';
  const isRootTimeline = !eventTimeline.parent;
  const eventGroupsByPortCall = useMemo(
    () =>
      isTcVoyage
        ? groupBy(
            sortBy(
              eventTimeline?.parent?.voyageGroups.flatMap(
                (x) => x.eventsAndLegs,
              ) || [],
              (x) => x.start,
            ),
            (td) => td.event.portCallId,
          )
        : {},
    [eventTimeline, isTcVoyage],
  );

  const voyageGroupsWithPortCall: EventsWithPortCalls[] = useMemo(
    () =>
      Object.keys(eventGroupsByPortCall).map((key) => ({
        portCallId: key,
        eventsAndLegs: eventGroupsByPortCall[key],
      })),
    [eventGroupsByPortCall],
  );

  const voyageGroupsWithOrderId = useMemo(
    () =>
      sortBy(
        eventTimeline.parent?.voyageGroups.map((x) => ({
          ...x,
          orderIds: x.orders.map((o) => o.id),
        })) || [],
        (x) => x.eventsAndLegs[0]?.start,
      ),
    [eventTimeline],
  );

  const [prevVoyageIndex, setPrevVoyageIndex] = useState<number>(0);
  const [nextVoyageIndex, setNextVoyageIndex] = useState<number>(0);

  const [eventsAndLegs, setEventsAndLegs] = useState(
    getSortedEventsAndLegs(eventTimeline.voyageGroups),
  );

  useEffect(() => {
    const numberOfPortCalls = Object.keys(
      groupBy(
        eventTimeline.voyageGroups.flatMap((x) => x.eventsAndLegs),
        (td) => td.event.portCallId,
      ),
    ).length;

    const getVoyageIndexWithOrderId = (orderId: number) => {
      return voyageGroupsWithOrderId.findIndex((x) =>
        x.orderIds.includes(orderId),
      );
    };

    const getVoyageIndexWithPortCallId = (portCallId: string) => {
      return voyageGroupsWithPortCall.findIndex(
        (x) => x.portCallId === portCallId,
      );
    };

    setEventsAndLegs(getSortedEventsAndLegs(eventTimeline.voyageGroups));
    const index = isTcVoyage
      ? getVoyageIndexWithPortCallId(
          eventTimeline.voyageGroups[0]?.portCalls[0]?.id || '',
        )
      : getVoyageIndexWithOrderId(
          eventTimeline.voyageGroups[0]?.orders[0]?.id || 0,
        );
    setPrevVoyageIndex(index);
    setNextVoyageIndex(index + (isTcVoyage ? numberOfPortCalls : 0));
  }, [
    eventTimeline,
    isTcVoyage,
    voyageGroupsWithOrderId,
    voyageGroupsWithPortCall,
  ]);

  const [vesselDateRange, setVesselDateRange] = useLocalSetting(
    'vesselDateRange',
    { from: undefined, to: undefined },
  );

  const firstStart = vesselDateRange.from;
  const lastEnd = vesselDateRange.to;

  // default - all focused events
  const [filters, setFilters] = useState<vesselFilterProps>({
    fromDate: isRootTimeline && firstStart ? new Date(firstStart) : null,
    toDate: isRootTimeline && lastEnd ? new Date(lastEnd) : null,
  });

  const prevEventsAndLegs = useMemo(() => {
    const { fromDate } = filters;

    return eventsAndLegs.filter((row: EventOrLeg<Events>) => {
      const start = row.start ?? null;
      const from = fromDate?.getTime() ?? null;
      return start && (from ? start < from : false);
    });
  }, [filters, eventsAndLegs]);

  const nextEventsAndLegs = useMemo(() => {
    const { toDate } = filters;

    return eventsAndLegs.filter((row: EventOrLeg<Events>) => {
      const start = row.start ?? null;
      const to = toDate?.getTime() ?? null;
      return start && (to ? start > to : false);
    });
  }, [filters, eventsAndLegs]);

  // TODO: restore functionality to expand selection
  const [nextOrderEventBefore, setNextOrderEventBefore] =
    useState<boolean>(false);
  const [nextOrderEventAfter, setNextOrderEventAfter] =
    useState<boolean>(false);

  const expandTimeRange = (
    direction: 'before' | 'after' | 'beforeAll' | 'afterAll' | 'prev' | 'next',
  ) => {
    // storaenso: used existing voyage groups
    if (direction === 'before') {
      if (prevEventsAndLegs.length) {
        const { fromDate, toDate } = filters;
        const prevFromDate = moment(fromDate).subtract(7, 'days').toDate();
        setFilters({ fromDate: prevFromDate, toDate });
      }
    } else if (direction === 'beforeAll') {
      if (prevEventsAndLegs.length) {
        setNextOrderEventBefore(true);
      }
    } else if (direction === 'after') {
      if (nextEventsAndLegs.length) {
        const { fromDate, toDate } = filters;
        const nextToDate = moment(toDate).add(7, 'days').toDate();
        setFilters({ fromDate, toDate: nextToDate });
      }
    } else if (direction === 'afterAll') {
      if (nextEventsAndLegs.length) {
        setNextOrderEventAfter(true);
      }
    } else {
      // neste (TC): generated group of voyages with groupBy portCall on eventsAndLegs
      if (
        (!isTcVoyage && !voyageGroupsWithOrderId) ||
        (isTcVoyage && !voyageGroupsWithPortCall)
      )
        return;

      const start =
        direction === 'prev' ? prevVoyageIndex - 1 : prevVoyageIndex;
      const end = direction === 'prev' ? nextVoyageIndex : nextVoyageIndex + 1;

      if (
        start < 0 ||
        (!isTcVoyage && end >= voyageGroupsWithOrderId.length) ||
        (isTcVoyage && end > voyageGroupsWithPortCall.length)
      )
        return;

      const sortedList = getSortedEventsAndLegs(
        !isTcVoyage
          ? voyageGroupsWithOrderId.slice(start, end + 1)
          : voyageGroupsWithPortCall.slice(start, end),
      );

      setFilters({
        fromDate: new Date(sortedList[0].start),
        toDate: new Date(sortedList[sortedList.length - 1].end),
      });

      setEventsAndLegs(sortedList);
      setPrevVoyageIndex(start);
      setNextVoyageIndex(end);
    }
  };

  // filtering eventsAndLegs
  const shownEventsAndLegs = useMemo(() => {
    const { fromDate, toDate } = filters;
    if (fromDate === null && toDate === null) {
      return eventsAndLegs;
    }

    let filtered = eventsAndLegs.filter((row: EventOrLeg<Events>) => {
      const start = row.start ?? null;
      return start && isInFilterRange(start, fromDate, toDate);
    });

    if (nextOrderEventBefore) filtered = [...prevEventsAndLegs, ...filtered];
    if (nextOrderEventAfter) filtered = [...filtered, ...nextEventsAndLegs];

    return filtered;
  }, [
    filters,
    eventsAndLegs,
    nextOrderEventBefore,
    nextOrderEventAfter,
    prevEventsAndLegs,
    nextEventsAndLegs,
  ]);

  const shownTimeData = useMemo(() => {
    return shownEventsAndLegs.filter(isTimeData);
  }, [shownEventsAndLegs]);

  const totals: TimeDataTotals = useMemo(
    () =>
      shownTimeData.reduce((acc, t) => {
        return {
          ballastDistance: 0,
          bunkerCost: (acc.bunkerCost ?? 0) + t.costs.bunkerGrade.total,
          portCost: (acc.portCost ?? 0) + t.costs.portCostEstimate,
          distance: (acc.distance ?? 0) + t.distance,
          delays: (acc.delays ?? 0) + t.delays,
          constrainedDelays: (acc.constrainedDelays ?? 0) + t.constrainedDelays,
          cost: (acc.cost ?? 0) + t.costs.totalCost,
          tcCost: (acc.tcCost ?? 0) + t.costs.tcCost,
          mass: (acc.mass ?? 0) + (t.mass > 0 ? t.mass : 0),
          totalHours: (acc.totalHours ?? 0) + t.totalHours,
          hoursAtPort: (acc.hoursAtPort ?? 0) + t.hoursAtPort,
          hoursAtSea: (acc.hoursAtSea ?? 0) + t.hoursAtSea,
        };
      }, initTotals()),
    [shownTimeData],
  );

  const isBerthPredictionAvailable = hasFeature('berth_prediction');
  const isVoyageCalculationAvailable = hasFeature('calculate_voyage');
  const isChannelUsageToggleAvailable = hasFeature('channel_usage_toggle');

  const berthPredictions = useBerthPrediction(
    isBerthPredictionAvailable,
    vessel,
    shownEventsAndLegs,
  );

  const voyageConsumptionData = useCalculateVoyages(
    isVoyageCalculationAvailable,
    vessel,
    shownEventsAndLegs,
  );

  const shownOrderIds = useMemo(
    () =>
      new Set(
        shownEventsAndLegs.flatMap((td) =>
          isTimeData(td) && isCargoEventTimeData(td) ? [td.event.order.id] : [],
        ),
      ),
    [shownEventsAndLegs],
  );

  const shownOrders = useMemo(
    () => orders.filter((o) => shownOrderIds.has(o.id)),
    [orders, shownOrderIds],
  );

  const isSingleShipment =
    new Set(focusedOrders.map((o) => o.shipmentId).filter(isDefined)).size ===
    1;

  const firstFocusedOrder = shownOrders.find((o) => focusedOrderIds.has(o.id));
  const shipment = firstFocusedOrder?.shipment ?? null;
  const shipmentDbId = shipment?.id ?? null;
  const externalShipmentDbId = shipment?.externalShipmentId ?? null;
  const isCommittedShipment = shipmentDbId !== null;

  const isFocusedShipmentShared =
    isSingleShipment &&
    shownEventsAndLegs.some(
      (td) =>
        isTimeData(td) &&
        isTCOrderEvent(td.event) &&
        td.event.entry.shared &&
        td.event.order.shipmentId &&
        focusedOrderIds.has(td.event.order.id),
    );

  const voyageNamingRuleByShipId = useVoyageNamingRules(
    currentUser.organizationId,
  );

  return (
    <View p={4}>
      <View>
        {isSingleShipment && isCommittedShipment && (
          <ShipmentOptions
            scenario={scenario}
            shownOrders={shownOrders}
            shipmentId={shipmentDbId}
            remarks={shownOrders.at(0)?.shipment?.remarks ?? ''}
            voyageNamingRuleByShipId={voyageNamingRuleByShipId}
            vessel={vessel}
            externalShipmentId={externalShipmentDbId}
          />
        )}
        <Box>
          {isVesselChangeEnabled && (
            <VesselChangeForm
              coaId={coaId}
              onVesselChange={onVesselChange}
              shipownerOrgId={
                eventTimeline.type === 'coa' || eventTimeline.type === 'spot'
                  ? eventTimeline.ownerOrganizationId
                  : null
              }
              vessel={vessel ?? null}
              voyageCharterId={voyageCharterId}
            />
          )}
          {voyageCharterId !== null ? (
            <ShipmentVoyageCharterUpdates
              voyageCharterId={voyageCharterId}
              onApprove={onVoyageCharterUpdateApprove}
            />
          ) : null}
        </Box>
        <ShippingPlanOrderTable
          focusedOrderIds={focusedOrderIds}
          orders={shownOrders}
          onUnassign={onUnassign}
          currentUser={currentUser}
          filters={filters}
          setFilters={!eventTimeline.parent ? setFilters : undefined}
          setVesselDateRange={setVesselDateRange}
          isReadOnlyScenario={isReadOnlyScenario}
        />
        {totals && (
          <PortRotationTable
            isEditable={isEditable}
            isFocusedShipmentShared={isFocusedShipmentShared}
            onCreateSingleEvent={onCreateSingleEvent}
            onEventChange={onEventChange}
            onDelete={onDelete}
            onSwap={onSwap}
            onSwapPortCall={onSwapPortCall}
            onOptimizeVoyageSpeed={onOptimizeVoyageSpeed}
            eventsAndLegs={shownEventsAndLegs}
            timeData={shownTimeData}
            totals={totals}
            inputs={shipInput}
            hasMoreEvents={[
              !nextOrderEventBefore && !!prevEventsAndLegs.length,
              !nextOrderEventAfter && !!nextEventsAndLegs.length,
            ]}
            onShowMoreEvents={expandTimeRange}
            eventTimeline={eventTimeline}
            currentUser={currentUser}
            prevVoyageIndex={prevVoyageIndex}
            nextVoyageIndex={nextVoyageIndex}
            voyageGroups={
              isTcVoyage ? voyageGroupsWithPortCall : voyageGroupsWithOrderId
            }
            berthPredictions={berthPredictions}
            isBerthPredictionAvailable={isBerthPredictionAvailable}
            isChannelUsageToggleAvailable={isChannelUsageToggleAvailable}
            vessel={vessel}
            voyageConsumptionData={voyageConsumptionData}
            scheduleContext={scheduleContext}
            shipment={shipment}
            isReadOnlyScenario={isReadOnlyScenario}
          />
        )}
      </View>
    </View>
  );
};

export default ShippingPlan;
